/**
 * Elite Batch 18 Part 3 — Workflow Templates 1688–1707
 * Unqualified lead long-term nurture, upsell opportunity detector, upsell play by
 * seat utilization, upsell recommendation bot, user conference lifecycle, webinar
 * attended next-step nurture, webinar attendee follow-up, webinar attendee
 * non-converter nurture, webinar day-before reminder, webinar hour-before reminder,
 * webinar invite series, webinar no-show follow-up, webinar no-show replay sequence,
 * webinar registration confirmation, webinar registration nurture, webinar reminder
 * 24h and 1h, whitepaper download nurture, workshop homework reminders, AE-SDR
 * handoff automation, AI cold call opener generator.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 18 PART 3 — TEMPLATES 1688–1707
// =============================================================================

export const ELITE_BATCH18_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1688. UNQUALIFIED LEAD LONG-TERM NURTURE (8 nodes)
  // ===================================================================

  "Unqualified Lead Long-Term Nurture": {
    summary:
      "Fires when a lead is marked unqualified via webhook, parses the disqualification reason with runCode, routes nurture segments via condition, sends a Month 1 value-add content email, waits 60 days, sends a Month 3 industry insights email, checks quarterly re-qualification via condition, and sends a Month 6 case study soft CTA email.",
    nodes: [
      trigger("Webhook: Lead marked unqualified", "webhook_app", {
        source: "hubspot",
        event: "contact.property_change.hs_lead_status",
        howItStarts:
          "Fires when a lead is explicitly marked as unqualified in the CRM by a sales rep — including timing mismatch, budget constraints, wrong persona, or not-a-fit designations — to begin a long-horizon nurture rather than abandoning the contact",
        setup:
          "Configure HubSpot webhook for lead status property changes filtered to unqualified statuses. Set up email sending credentials. Ensure nurture content library is populated with value-add, industry, and case study assets.",
        inputFields: [],
      }),

      // 2. Disqualification reason parser runCode
      n("2", "runCode", "Parse Disqualification Reason", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the webhook payload to extract disqualification details
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.properties?.email || payload.contact?.email || "";
const name = payload.firstname || payload.properties?.firstname || payload.contact?.firstname || "";
const company = payload.company || payload.properties?.company || "";
const reason = payload.hs_lead_status || payload.properties?.hs_lead_status || "unqualified";
const owner = payload.hubspot_owner_id || payload.properties?.hubspot_owner_id || "";
const disqualNote = payload.notes_last_contacted || payload.properties?.notes_last_contacted || "";
let nurture_segment = "general_nurture";
let requalification_months = 6;
const reasonLower = reason.toLowerCase();
if (reasonLower.includes("timing") || reasonLower.includes("not now") || reasonLower.includes("later")) {
  nurture_segment = "timing_mismatch";
  requalification_months = 3;
} else if (reasonLower.includes("budget") || reasonLower.includes("price") || reasonLower.includes("cost")) {
  nurture_segment = "budget_constrained";
  requalification_months = 6;
} else if (reasonLower.includes("competitor") || reasonLower.includes("alternative")) {
  nurture_segment = "competitor_chosen";
  requalification_months = 4;
} else if (reasonLower.includes("persona") || reasonLower.includes("fit") || reasonLower.includes("wrong")) {
  nurture_segment = "wrong_persona";
  requalification_months = 12;
}
return {
  lead_email: email,
  lead_name: name,
  lead_company: company,
  disqualification_reason: reason,
  disqual_note: disqualNote,
  owner_id: owner,
  nurture_segment: nurture_segment,
  requalification_months: requalification_months,
  is_timing_based: nurture_segment === "timing_mismatch" ? "true" : "false"
};`,
          outputVariables: [
            { name: "lead_email", type: "string" },
            { name: "lead_name", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "disqualification_reason", type: "string" },
            { name: "disqual_note", type: "string" },
            { name: "owner_id", type: "string" },
            { name: "nurture_segment", type: "string" },
            { name: "requalification_months", type: "number" },
            { name: "is_timing_based", type: "string" },
          ],
        },
      }),

      // 3. Nurture segment assignment condition
      n("3", "condition", "Is Timing-Based Disqualification?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "is_timing_based", operator: "eq", value: "true" },
          ],
        },
      }),

      // 4. Month 1 value-add content email
      emailNode(
        "4",
        "Month 1: Value-Add Content Email",
        760,
        "{lead_email}",
        "{lead_name}, fresh insights for {lead_company} in the new quarter",
        "<p>Hi {lead_name},</p><p>We know the timing was not quite right when we last spoke, but we wanted to stay on your radar with some genuinely useful content.</p><p>Here are three resources that teams at companies like {lead_company} have found valuable:</p><ul><li><strong>Industry Benchmark Report:</strong> See how peers in your space are tackling the exact challenges we discussed</li><li><strong>Best Practices Guide:</strong> Actionable strategies you can implement today, regardless of what tools you are using</li><li><strong>Expert Webinar Recording:</strong> A 20-minute session on emerging trends that impact your team</li></ul><p>No sales pitch — just genuinely useful material. If anything resonates, feel free to reply.</p><p>Cheers,<br/>The Team</p>",
      ),

      // 5. Delay 60 days
      n("5", "delayAction", "Wait 60 Days Before Month 3 Touch", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 60,
          delayUnit: "days",
          description:
            "Wait 60 days after Month 1 email to allow the lead to organically re-engage before sending the Month 3 industry insights touch",
        },
      }),

      // 6. Month 3 industry insights email
      emailNode(
        "6",
        "Month 3: Industry Insights Email",
        1240,
        "{lead_email}",
        "What changed in your industry since we last connected, {lead_name}",
        "<p>Hi {lead_name},</p><p>A lot has shifted in the past few months. Here is a quick snapshot of what we are seeing across teams like yours at {lead_company}:</p><ul><li><strong>Trend #1:</strong> Companies are increasingly consolidating their tooling to reduce operational overhead by 30-40%</li><li><strong>Trend #2:</strong> Automation adoption has doubled year-over-year for teams your size</li><li><strong>Trend #3:</strong> The cost of inaction is growing — teams that delayed modernization are now spending 2x to catch up</li></ul><p>We have compiled an in-depth analysis with data from 500+ organizations. Reply if you would like a copy tailored to {lead_company}.</p><p>Best,<br/>The Team</p>",
      ),

      // 7. Quarterly re-qualification check condition
      n("7", "condition", "Is Lead Ready for Re-Qualification?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "nurture_segment",
              operator: "neq",
              value: "wrong_persona",
            },
          ],
        },
      }),

      // 8. Month 6 case study soft CTA email
      emailNode(
        "8",
        "Month 6: Case Study Soft CTA Email",
        1720,
        "{lead_email}",
        "{lead_name}, how {lead_company} could replicate this success",
        "<p>Hi {lead_name},</p><p>It has been about six months since we last connected, and we wanted to share a case study that might resonate with your situation at {lead_company}.</p><p><strong>Case Study: How a team in your space cut their workflow time by 65%</strong></p><p>The kicker? They were in a similar position to yours when we first spoke — the timing was not right. Six months later, they revisited the decision and here is what happened:</p><ul><li>65% reduction in manual workflow time</li><li>3x increase in team throughput</li><li>Full ROI in under 90 days</li></ul><p>If your priorities have evolved or you are heading into a new planning cycle, I would love to reconnect for a quick 15-minute chat — no pressure.</p><p><a href='https://calendly.com/your-team/reconnect?email={lead_email}'>Grab a time here</a> or simply reply to this email.</p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1689. UPSELL OPPORTUNITY DETECTOR (8 nodes)
  // ===================================================================

  "Upsell Opportunity Detector": {
    summary:
      "Fires when a usage threshold is crossed via webhook, evaluates thresholds with runCode, enriches account context from HubSpot, generates AI upsell recommendations, prioritizes signals via condition, alerts CSM and AE on Slack, sends an automated product nudge email, and tracks the opportunity in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Usage threshold crossed", "webhook_app", {
        source: "product_analytics",
        event: "usage.threshold_exceeded",
        howItStarts:
          "Fires when a customer account crosses a predefined usage threshold — such as 80% seat utilization, API call limits approaching, storage nearing capacity, or feature adoption milestones — signaling upsell readiness",
        setup:
          "Configure product analytics platform to emit webhook when usage thresholds are crossed. Set OpenAI API key for AI recommendations. Provide HubSpot token for account lookup and opportunity creation. Configure Slack webhook for CSM/AE notifications.",
        inputFields: [],
      }),

      // 2. Threshold evaluation engine runCode
      n("2", "runCode", "Evaluate Usage Threshold Signals", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and evaluate usage threshold signals
const payload = JSON.parse(vars.webhook_payload || "{}");
const account_id = payload.account_id || payload.company_id || payload.customer_id || "";
const account_email = payload.email || payload.admin_email || payload.contact_email || "";
const account_name = payload.company_name || payload.account_name || "";
const metric_name = payload.metric || payload.threshold_name || "usage";
const current_value = parseFloat(payload.current_value || payload.value || "0");
const threshold_value = parseFloat(payload.threshold || payload.limit || "100");
const plan_name = payload.plan || payload.subscription || "standard";
const utilization_pct = threshold_value > 0 ? Math.round((current_value / threshold_value) * 100) : 0;
let urgency = "low";
let upsell_type = "expansion";
if (utilization_pct >= 95) { urgency = "critical"; upsell_type = "immediate_upgrade"; }
else if (utilization_pct >= 85) { urgency = "high"; upsell_type = "proactive_upgrade"; }
else if (utilization_pct >= 75) { urgency = "medium"; upsell_type = "expansion"; }
const is_high_priority = (urgency === "critical" || urgency === "high") ? "true" : "false";
return {
  account_id: account_id,
  account_email: account_email,
  account_name: account_name,
  metric_name: metric_name,
  current_value: String(current_value),
  threshold_value: String(threshold_value),
  utilization_pct: String(utilization_pct),
  plan_name: plan_name,
  urgency: urgency,
  upsell_type: upsell_type,
  is_high_priority: is_high_priority
};`,
          outputVariables: [
            { name: "account_id", type: "string" },
            { name: "account_email", type: "string" },
            { name: "account_name", type: "string" },
            { name: "metric_name", type: "string" },
            { name: "current_value", type: "string" },
            { name: "threshold_value", type: "string" },
            { name: "utilization_pct", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "urgency", type: "string" },
            { name: "upsell_type", type: "string" },
            { name: "is_high_priority", type: "string" },
          ],
        },
      }),

      // 3. Account context enrichment HubSpot
      hubspotGetNode(
        "3",
        "Enrich Account Context from HubSpot",
        520,
        "crm/v3/objects/companies/search?query={account_name}",
        [
          {
            name: "results[0].id",
            customName: "hs_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.name",
            customName: "hs_company_name",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "annual_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "employee_count",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "account_status",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "csm_owner_id",
            type: "string",
          },
        ],
      ),

      // 4. AI upsell recommendation
      aiNode(
        "4",
        "AI Generate Upsell Recommendation",
        760,
        "You are a B2B customer success upsell strategist. Given account usage data, current plan, utilization percentage, and account context, recommend the best upsell path. Consider the urgency level, the specific metric that triggered the alert, and the account size. Recommend specific plan tiers, add-ons, or expansions. Respond JSON: {recommended_plan: string, upsell_pitch: string, value_proposition: string, estimated_expansion_arr: string, talk_track: string, risk_if_no_action: string, email_subject: string, email_body_html: string}",
        "Account: {account_name} (ID: {account_id}). Current plan: {plan_name}. Metric: {metric_name} at {utilization_pct}% ({current_value}/{threshold_value}). Urgency: {urgency}. Upsell type: {upsell_type}. Revenue: ${annual_revenue}. Employees: {employee_count}. Account status: {account_status}. Generate the optimal upsell recommendation.",
        [
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          { field: "upsell_pitch", as: "upsell_pitch", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "estimated_expansion_arr",
            as: "estimated_expansion_arr",
            type: "string",
          },
          { field: "talk_track", as: "talk_track", type: "string" },
          {
            field: "risk_if_no_action",
            as: "risk_if_no_action",
            type: "string",
          },
          {
            field: "email_subject",
            as: "upsell_email_subject",
            type: "string",
          },
          { field: "email_body_html", as: "upsell_email_body", type: "string" },
        ],
      ),

      // 5. Signal prioritization condition
      n("5", "condition", "Is High-Priority Upsell Signal?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_priority", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. CSM AE alert Slack
      slackNode(
        "6",
        "Alert CSM and AE on Slack",
        1240,
        "Upsell Signal Detected for *{account_name}*\\nMetric: {metric_name} at *{utilization_pct}%* utilization ({current_value}/{threshold_value})\\nUrgency: *{urgency}* | Current plan: {plan_name}\\nRecommended: *{recommended_plan}* (est. +${estimated_expansion_arr} ARR)\\nPitch: {upsell_pitch}\\nRisk if no action: {risk_if_no_action}\\nTalk Track: {talk_track}",
      ),

      // 7. Automated nudge email
      emailNode(
        "7",
        "Send Upsell Nudge Email",
        1480,
        "{account_email}",
        "{upsell_email_subject}",
        "{upsell_email_body}",
      ),

      // 8. CRM opportunity tracking HubSpot
      hubspotPostNode(
        "8",
        "Create Upsell Opportunity in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Upsell: {account_name} - {recommended_plan}","pipeline":"default","dealstage":"qualifiedtobuy","amount":"{estimated_expansion_arr}","hubspot_owner_id":"{csm_owner_id}","deal_type":"EXISTING_BUSINESS","description":"Auto-detected upsell: {metric_name} at {utilization_pct}% for {account_name}. Recommended: {recommended_plan}. Value prop: {value_proposition}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "deal_created",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1690. UPSELL PLAY BY SEAT UTILIZATION (7 nodes)
  // ===================================================================

  "Upsell Play by Seat Utilization": {
    summary:
      "Fires when a seat utilization monitor triggers via webhook, analyzes utilization trends with runCode, prioritizes accounts via condition, generates AI upsell messaging, notifies the CSM with playbook on Slack, sends a customer notification email, and tracks expansion in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Seat utilization threshold reached", "webhook_app", {
        source: "product_analytics",
        event: "seats.utilization_alert",
        howItStarts:
          "Fires when an account reaches 80% or higher seat utilization — indicating the team is outgrowing their current plan and is a strong candidate for seat expansion or tier upgrade",
        setup:
          "Configure product analytics to emit webhook when seat utilization crosses 80%. Set OpenAI API key for AI messaging. Provide HubSpot token for CRM expansion tracking. Configure Slack webhook for CSM notifications.",
        inputFields: [],
      }),

      // 2. Utilization trend analysis runCode
      n("2", "runCode", "Analyze Seat Utilization Trends", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse seat utilization data and assess expansion readiness
const payload = JSON.parse(vars.webhook_payload || "{}");
const account_id = payload.account_id || payload.company_id || "";
const account_name = payload.company_name || payload.account_name || "";
const account_email = payload.admin_email || payload.contact_email || "";
const total_seats = parseInt(payload.total_seats || payload.licensed_seats || "0");
const used_seats = parseInt(payload.used_seats || payload.active_seats || "0");
const pending_invites = parseInt(payload.pending_invites || "0");
const plan_name = payload.plan || payload.tier || "standard";
const monthly_cost = parseFloat(payload.mrr || payload.monthly_cost || "0");
const utilization = total_seats > 0 ? Math.round((used_seats / total_seats) * 100) : 0;
const effective_utilization = total_seats > 0 ? Math.round(((used_seats + pending_invites) / total_seats) * 100) : 0;
const seats_remaining = total_seats - used_seats;
const cost_per_seat = total_seats > 0 ? Math.round(monthly_cost / total_seats) : 0;
let expansion_urgency = "low";
if (effective_utilization >= 95) expansion_urgency = "critical";
else if (effective_utilization >= 85) expansion_urgency = "high";
else if (effective_utilization >= 75) expansion_urgency = "medium";
const is_expansion_ready = (expansion_urgency === "critical" || expansion_urgency === "high") ? "true" : "false";
return {
  account_id: account_id,
  account_name: account_name,
  account_email: account_email,
  total_seats: String(total_seats),
  used_seats: String(used_seats),
  pending_invites: String(pending_invites),
  seats_remaining: String(seats_remaining),
  utilization_pct: String(utilization),
  effective_utilization_pct: String(effective_utilization),
  plan_name: plan_name,
  monthly_cost: String(monthly_cost),
  cost_per_seat: String(cost_per_seat),
  expansion_urgency: expansion_urgency,
  is_expansion_ready: is_expansion_ready
};`,
          outputVariables: [
            { name: "account_id", type: "string" },
            { name: "account_name", type: "string" },
            { name: "account_email", type: "string" },
            { name: "total_seats", type: "string" },
            { name: "used_seats", type: "string" },
            { name: "pending_invites", type: "string" },
            { name: "seats_remaining", type: "string" },
            { name: "utilization_pct", type: "string" },
            { name: "effective_utilization_pct", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "monthly_cost", type: "string" },
            { name: "cost_per_seat", type: "string" },
            { name: "expansion_urgency", type: "string" },
            { name: "is_expansion_ready", type: "string" },
          ],
        },
      }),

      // 3. Account prioritization condition
      n("3", "condition", "Is Account Expansion-Ready?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "is_expansion_ready", operator: "eq", value: "true" },
          ],
        },
      }),

      // 4. AI upsell messaging
      aiNode(
        "4",
        "AI Generate Seat Expansion Messaging",
        760,
        "You are a customer success expansion specialist. Given seat utilization data for an account, craft a non-pushy, value-focused expansion message. Highlight the risk of running out of seats, the benefit of proactive expansion (volume discounts, uninterrupted access), and recommend a specific seat count increase. Respond JSON: {recommended_seats: string, discount_offer: string, email_subject: string, email_body_html: string, csm_playbook: string, expansion_value_prop: string}",
        "Account: {account_name}. Plan: {plan_name}. Seats: {used_seats}/{total_seats} ({utilization_pct}% utilized). Pending invites: {pending_invites}. Effective utilization: {effective_utilization_pct}%. Cost/seat: ${cost_per_seat}/mo. Urgency: {expansion_urgency}. Remaining seats: {seats_remaining}. Generate seat expansion messaging.",
        [
          {
            field: "recommended_seats",
            as: "recommended_seats",
            type: "string",
          },
          { field: "discount_offer", as: "discount_offer", type: "string" },
          { field: "email_subject", as: "expansion_subject", type: "string" },
          { field: "email_body_html", as: "expansion_body", type: "string" },
          { field: "csm_playbook", as: "csm_playbook", type: "string" },
          {
            field: "expansion_value_prop",
            as: "expansion_value_prop",
            type: "string",
          },
        ],
      ),

      // 5. CSM notification with playbook Slack
      slackNode(
        "5",
        "Notify CSM with Expansion Playbook",
        1000,
        "Seat Expansion Opportunity: *{account_name}*\\nUtilization: *{used_seats}/{total_seats}* seats ({utilization_pct}%) | Pending invites: {pending_invites}\\nEffective utilization: {effective_utilization_pct}% | Urgency: *{expansion_urgency}*\\nPlan: {plan_name} at ${monthly_cost}/mo (${cost_per_seat}/seat)\\nRecommendation: Add {recommended_seats} seats | Discount: {discount_offer}\\nPlaybook: {csm_playbook}",
      ),

      // 6. Customer notification email
      emailNode(
        "6",
        "Send Seat Expansion Email to Customer",
        1240,
        "{account_email}",
        "{expansion_subject}",
        "{expansion_body}",
      ),

      // 7. CRM expansion tracking HubSpot
      hubspotPostNode(
        "7",
        "Track Expansion Opportunity in HubSpot",
        1480,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Seat Expansion: {account_name} +{recommended_seats} seats","pipeline":"default","dealstage":"appointmentscheduled","deal_type":"EXISTING_BUSINESS","description":"Seat utilization at {utilization_pct}% ({used_seats}/{total_seats}). Recommended +{recommended_seats} seats. Discount: {discount_offer}. Value prop: {expansion_value_prop}"}',
          },
        ],
        [
          { name: "id", customName: "expansion_deal_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "deal_created_at",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1691. UPSELL RECOMMENDATION BOT (8 nodes)
  // ===================================================================

  "Upsell Recommendation Bot": {
    summary:
      "Fires when a Slack query requests upsell intel via webhook, retrieves account data from HubSpot, analyzes usage patterns with runCode, generates AI recommendations, builds competitive positioning with runCode, delivers an interactive Slack response, routes action execution via condition, and tracks recommendations in Google Sheets.",
    nodes: [
      trigger("Webhook: Slack upsell query received", "webhook_app", {
        source: "slack",
        event: "slash_command.upsell",
        howItStarts:
          "Fires when a CSM or AE triggers the /upsell slash command in Slack with an account name — requesting real-time upsell intelligence, recommendations, and competitive positioning for an upcoming customer conversation",
        setup:
          "Configure Slack slash command /upsell to post to webhook URL. Set HubSpot API token for account data retrieval. Set OpenAI API key for AI recommendations. Configure Google Sheets for recommendation tracking.",
        inputFields: [],
      }),

      // 2. Account data retrieval HubSpot
      n("2", "runCode", "Parse Slack Command and Extract Account", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the Slack slash command payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const command_text = payload.text || payload.command_text || "";
const requester_name = payload.user_name || payload.user?.name || "";
const requester_id = payload.user_id || payload.user?.id || "";
const channel_id = payload.channel_id || "";
const response_url = payload.response_url || "";
const account_query = command_text.trim();
return {
  account_query: account_query,
  requester_name: requester_name,
  requester_id: requester_id,
  channel_id: channel_id,
  response_url: response_url
};`,
          outputVariables: [
            { name: "account_query", type: "string" },
            { name: "requester_name", type: "string" },
            { name: "requester_id", type: "string" },
            { name: "channel_id", type: "string" },
            { name: "response_url", type: "string" },
          ],
        },
      }),

      // 3. HubSpot account data retrieval
      hubspotGetNode(
        "3",
        "Retrieve Account Data from HubSpot",
        520,
        "crm/v3/objects/companies/search?query={account_query}",
        [
          {
            name: "results[0].id",
            customName: "hs_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.name",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "arr",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "employees",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "account_status",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "open_deals",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contact_notes",
            type: "string",
          },
        ],
      ),

      // 4. Usage pattern analysis runCode
      n("4", "runCode", "Analyze Usage Patterns for Upsell Signals", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze account data to identify upsell patterns
const arr = parseFloat(vars.arr) || 0;
const employees = parseInt(vars.employees) || 0;
const openDeals = parseInt(vars.open_deals) || 0;
const industry = (vars.industry || "").toLowerCase();
const status = (vars.account_status || "").toLowerCase();
let arr_per_employee = employees > 0 ? Math.round(arr / employees) : 0;
let expansion_potential = "low";
if (arr_per_employee < 100 && employees > 50) expansion_potential = "high";
else if (arr_per_employee < 200) expansion_potential = "medium";
let health_signal = "healthy";
if (status.includes("at risk") || status.includes("churn")) health_signal = "at_risk";
else if (status.includes("champion") || status.includes("advocate")) health_signal = "champion";
const has_open_deals = openDeals > 0 ? "true" : "false";
const recommended_approach = health_signal === "at_risk" ? "retention_first" : expansion_potential === "high" ? "aggressive_expansion" : "gentle_nudge";
return {
  arr_per_employee: String(arr_per_employee),
  expansion_potential: expansion_potential,
  health_signal: health_signal,
  has_open_deals: has_open_deals,
  recommended_approach: recommended_approach,
  account_summary: vars.company_name + " | $" + arr + " ARR | " + employees + " employees | " + industry
};`,
          outputVariables: [
            { name: "arr_per_employee", type: "string" },
            { name: "expansion_potential", type: "string" },
            { name: "health_signal", type: "string" },
            { name: "has_open_deals", type: "string" },
            { name: "recommended_approach", type: "string" },
            { name: "account_summary", type: "string" },
          ],
        },
      }),

      // 5. AI recommendation engine
      aiNode(
        "5",
        "AI Generate Upsell Recommendation",
        1000,
        "You are an expert B2B upsell strategist. Given account data including ARR, employee count, industry, health signals, and expansion potential, generate a comprehensive upsell recommendation. Include specific products or tiers to recommend, objection handling tips, competitive intelligence, and a conversation opener. Respond JSON: {recommendation_summary: string, products_to_recommend: string, conversation_opener: string, objection_handlers: string, competitive_positioning: string, estimated_deal_size: string, confidence_score: string, next_best_action: string}",
        "Account: {company_name}. ARR: ${arr}. Employees: {employees}. Industry: {industry}. Status: {account_status}. ARR/employee: ${arr_per_employee}. Expansion potential: {expansion_potential}. Health: {health_signal}. Open deals: {open_deals}. Approach: {recommended_approach}. Last notes: {last_contact_notes}. Generate comprehensive upsell recommendation.",
        [
          {
            field: "recommendation_summary",
            as: "recommendation_summary",
            type: "string",
          },
          {
            field: "products_to_recommend",
            as: "products_to_recommend",
            type: "string",
          },
          {
            field: "conversation_opener",
            as: "conversation_opener",
            type: "string",
          },
          {
            field: "objection_handlers",
            as: "objection_handlers",
            type: "string",
          },
          {
            field: "competitive_positioning",
            as: "competitive_positioning",
            type: "string",
          },
          {
            field: "estimated_deal_size",
            as: "estimated_deal_size",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "string" },
          { field: "next_best_action", as: "next_best_action", type: "string" },
        ],
      ),

      // 6. Competitive positioning runCode
      n("6", "runCode", "Build Competitive Positioning Brief", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile the full recommendation into a formatted Slack message
const summary = vars.recommendation_summary || "";
const products = vars.products_to_recommend || "";
const opener = vars.conversation_opener || "";
const objections = vars.objection_handlers || "";
const competitive = vars.competitive_positioning || "";
const dealSize = vars.estimated_deal_size || "";
const confidence = vars.confidence_score || "";
const nextAction = vars.next_best_action || "";
const slackMessage = "*Upsell Intel for " + vars.company_name + "*\\n" +
  "Requested by: <@" + vars.requester_id + ">\\n\\n" +
  "*Summary:* " + summary + "\\n" +
  "*Products:* " + products + "\\n" +
  "*Est. Deal Size:* $" + dealSize + " | Confidence: " + confidence + "\\n\\n" +
  "*Conversation Opener:*\\n" + opener + "\\n\\n" +
  "*Objection Handlers:*\\n" + objections + "\\n\\n" +
  "*Competitive Positioning:*\\n" + competitive + "\\n\\n" +
  "*Next Best Action:* " + nextAction;
const should_create_deal = (vars.has_open_deals === "false" && vars.expansion_potential !== "low") ? "true" : "false";
return {
  formatted_slack_message: slackMessage,
  should_create_deal: should_create_deal
};`,
          outputVariables: [
            { name: "formatted_slack_message", type: "string" },
            { name: "should_create_deal", type: "string" },
          ],
        },
      }),

      // 7. Interactive response Slack + action execution condition
      slackNode(
        "7",
        "Deliver Upsell Intel to Slack",
        1480,
        "{formatted_slack_message}",
      ),

      // 8. Recommendation tracking Sheets
      sheetsNode(
        "8",
        "Log Recommendation to Sheets",
        1720,
        "UpsellRecommendations",
        '[["{company_name}","{arr}","{employees}","{expansion_potential}","{health_signal}","{products_to_recommend}","{estimated_deal_size}","{confidence_score}","{requester_name}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1692. USER CONFERENCE LIFECYCLE (10 nodes)
  // ===================================================================

  "User Conference Lifecycle": {
    summary:
      "Fires on conference registration via webhook, syncs attendee to HubSpot CRM, sends a confirmation welcome package email, delivers pre-conference engagement emails, manages session capacity with runCode, tracks day-of check-ins via API, sends real-time engagement alerts on Slack, sends a post-event survey email, delivers post-event follow-up emails, and logs ROI attribution to Google Sheets.",
    nodes: [
      trigger("Webhook: Conference registration received", "webhook_app", {
        source: "eventbrite",
        event: "attendee.registered",
        howItStarts:
          "Fires when an attendee registers for the annual user conference — including registration details, ticket type, selected sessions, and contact information — to kick off the full conference lifecycle journey",
        setup:
          "Configure Eventbrite webhook for attendee registration events. Set HubSpot token for CRM sync. Configure email sending credentials. Set up Slack webhook for day-of alerts. Configure Google Sheets for ROI tracking.",
        inputFields: [],
      }),

      // 2. AI extract registration data + CRM sync
      aiNode(
        "2",
        "AI Extract Registration Data",
        280,
        "You are a conference registration parser. Extract the attendee name, email, phone, company, job title, ticket type, selected session IDs, dietary restrictions, travel needs, registration date, ticket amount paid, and promotional code used from the raw webhook payload. Respond JSON: {attendee_name: string, attendee_email: string, attendee_phone: string, attendee_company: string, attendee_title: string, ticket_type: string, selected_sessions: string, dietary_restrictions: string, travel_needs: string, registration_date: string, ticket_amount: string, promo_code: string}",
        "Raw webhook payload: {webhook_payload}. Extract all conference registration data regardless of field naming conventions.",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_phone", as: "attendee_phone", type: "string" },
          { field: "attendee_company", as: "attendee_company", type: "string" },
          { field: "attendee_title", as: "attendee_title", type: "string" },
          { field: "ticket_type", as: "ticket_type", type: "string" },
          {
            field: "selected_sessions",
            as: "selected_sessions",
            type: "string",
          },
          {
            field: "dietary_restrictions",
            as: "dietary_restrictions",
            type: "string",
          },
          { field: "travel_needs", as: "travel_needs", type: "string" },
          {
            field: "registration_date",
            as: "registration_date",
            type: "string",
          },
          { field: "ticket_amount", as: "ticket_amount", type: "string" },
          { field: "promo_code", as: "promo_code", type: "string" },
        ],
      ),

      // 3. CRM attendee sync HubSpot
      hubspotPostNode(
        "3",
        "Sync Attendee to HubSpot CRM",
        520,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{attendee_email}","firstname":"{attendee_name}","company":"{attendee_company}","jobtitle":"{attendee_title}","phone":"{attendee_phone}","hs_lead_status":"CONFERENCE_REGISTRANT","notes_last_contacted":"Registered for user conference. Ticket: {ticket_type}. Sessions: {selected_sessions}. Amount: ${ticket_amount}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_synced_at",
            type: "string",
          },
        ],
      ),

      // 4. Confirmation welcome package email
      emailNode(
        "4",
        "Send Confirmation Welcome Package",
        760,
        "{attendee_email}",
        "You are confirmed for the User Conference, {attendee_name}!",
        "<p>Hi {attendee_name},</p><p>Welcome! Your registration for the User Conference is confirmed.</p><p><strong>Registration Details:</strong></p><ul><li>Ticket Type: {ticket_type}</li><li>Selected Sessions: {selected_sessions}</li><li>Amount: ${ticket_amount}</li></ul><p><strong>What to Expect:</strong></p><ul><li>Keynote sessions from industry leaders</li><li>Hands-on workshops tailored to your role as {attendee_title}</li><li>Networking opportunities with peers from {attendee_company} and beyond</li><li>Exclusive access to product roadmap previews</li></ul><p><strong>Next Steps:</strong></p><ol><li>Download the conference app and create your profile</li><li>Review the full session catalog and finalize your schedule</li><li>Join the attendee Slack channel for pre-event networking</li></ol><p>We cannot wait to see you there!</p><p>The Conference Team</p>",
      ),

      // 5. Pre-conference engagement series email
      emailNode(
        "5",
        "Pre-Conference Engagement Email",
        1000,
        "{attendee_email}",
        "{attendee_name}, start preparing for an incredible conference experience",
        "<p>Hi {attendee_name},</p><p>The conference is approaching and we want to make sure you get the most out of your experience.</p><p><strong>For Your Role as {attendee_title}:</strong></p><ul><li>Recommended sessions based on your track: {selected_sessions}</li><li>Pre-read materials to maximize your workshop time</li><li>Connect with speakers ahead of time in the attendee portal</li></ul><p><strong>Networking Tip:</strong> Update your attendee profile now so other attendees from companies like {attendee_company} can find you. The best connections often happen before the event starts.</p><p><strong>Logistics Reminder:</strong></p><ul><li>Conference venue and map available in the app</li><li>Your dietary preferences ({dietary_restrictions}) have been noted</li><li>Travel and accommodation details in your confirmation portal</li></ul><p>See you soon!</p><p>The Conference Team</p>",
      ),

      // 6. Session capacity management runCode
      n("6", "runCode", "Manage Session Capacity Tracking", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track session registrations and flag capacity concerns
const sessions = (vars.selected_sessions || "").split(",").map(s => s.trim()).filter(Boolean);
const ticketType = vars.ticket_type || "general";
const isVIP = ticketType.toLowerCase().includes("vip") || ticketType.toLowerCase().includes("premium");
const sessionCount = sessions.length;
const hasWorkshops = sessions.some(s => s.toLowerCase().includes("workshop"));
const hasKeynote = sessions.some(s => s.toLowerCase().includes("keynote"));
const attendeeSegment = isVIP ? "vip" : hasWorkshops ? "hands_on" : "general";
const engagementScore = (isVIP ? 30 : 0) + (sessionCount * 10) + (hasWorkshops ? 20 : 0);
return {
  session_count: String(sessionCount),
  session_list: sessions.join(", "),
  attendee_segment: attendeeSegment,
  is_vip: isVIP ? "true" : "false",
  has_workshops: hasWorkshops ? "true" : "false",
  has_keynote: hasKeynote ? "true" : "false",
  pre_event_engagement_score: String(engagementScore)
};`,
          outputVariables: [
            { name: "session_count", type: "string" },
            { name: "session_list", type: "string" },
            { name: "attendee_segment", type: "string" },
            { name: "is_vip", type: "string" },
            { name: "has_workshops", type: "string" },
            { name: "has_keynote", type: "string" },
            { name: "pre_event_engagement_score", type: "string" },
          ],
        },
      }),

      // 7. Day-of check-in tracking API
      n("7", "hitApi", "Track Day-Of Check-In via API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.eventbrite.com/v3/events/{event_id}/attendees/{attendee_id}/checkin",
          headers: [
            { key: "Authorization", value: "Bearer {eventbrite_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "attendee_email", value: "{attendee_email}" },
            { key: "check_in_type", value: "auto" },
          ],
          variables: [
            {
              name: "checked_in",
              customName: "checkin_confirmed",
              type: "boolean",
            },
            {
              name: "check_in_time",
              customName: "checkin_timestamp",
              type: "string",
            },
          ],
        },
      }),

      // 8. Real-time engagement alerts Slack
      slackNode(
        "8",
        "Send Real-Time Engagement Alert",
        1720,
        "Conference Check-In: *{attendee_name}* ({attendee_company})\\nTitle: {attendee_title} | Ticket: {ticket_type} | Segment: {attendee_segment}\\nSessions: {session_list} ({session_count} total)\\nVIP: {is_vip} | Pre-event score: {pre_event_engagement_score}\\nCheck-in confirmed: {checkin_confirmed} at {checkin_timestamp}",
      ),

      // 9. Post-event survey email
      emailNode(
        "9",
        "Send Post-Event Survey Email",
        1960,
        "{attendee_email}",
        "How was your conference experience, {attendee_name}?",
        "<p>Hi {attendee_name},</p><p>Thank you for attending the User Conference! We hope your experience was valuable.</p><p>Your feedback is incredibly important to us. Please take 3 minutes to share your thoughts:</p><p><a href='https://survey.yourcompany.com/conference?email={attendee_email}&segment={attendee_segment}'><strong>Take the Post-Conference Survey</strong></a></p><p>We would love to hear about:</p><ul><li>Your favorite sessions (you attended {session_count})</li><li>Networking quality and connections made</li><li>What you would change for next year</li><li>Topics you want us to cover going forward</li></ul><p>As a thank you for your feedback, we will send you exclusive recordings of any sessions you may have missed.</p><p>See you next year!</p><p>The Conference Team</p>",
      ),

      // 10. ROI attribution tracking Sheets
      sheetsNode(
        "10",
        "Log Conference ROI Attribution",
        2200,
        "ConferenceROI",
        '[["{attendee_name}","{attendee_email}","{attendee_company}","{attendee_title}","{ticket_type}","{ticket_amount}","{session_count}","{attendee_segment}","{pre_event_engagement_score}","{checkin_confirmed}","{registration_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1693. WEBINAR ATTENDED NEXT-STEP NURTURE (8 nodes)
  // ===================================================================

  "Webinar Attended Next-Step Nurture": {
    summary:
      "Fires when a webinar ends via webhook, scores attendee engagement with runCode, logs activity to HubSpot CRM, routes by segment via condition, sends a Day 1 recording and next-step email, waits 2 days, sends a Day 3 deeper dive email, and sends a Day 7 final CTA social proof email.",
    nodes: [
      trigger("Webhook: Webinar ended with attendee data", "webhook_app", {
        source: "zoom",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar concludes and the platform provides attendee engagement data — including attendance duration, poll responses, Q&A participation, and chat activity — to trigger a tailored post-webinar nurture sequence",
        setup:
          "Configure Zoom webhook for webinar.ended events. Set OpenAI API key for AI extraction. Provide HubSpot token for CRM activity logging. Configure email sending credentials for the nurture sequence.",
        inputFields: [],
      }),

      // 2. Engagement scoring runCode
      n("2", "runCode", "Score Webinar Attendee Engagement", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and score attendee engagement
const payload = JSON.parse(vars.webhook_payload || "{}");
const attendee_email = payload.attendee_email || payload.email || payload.participant?.email || "";
const attendee_name = payload.attendee_name || payload.name || payload.participant?.name || "";
const webinar_title = payload.webinar_title || payload.topic || payload.webinar?.topic || "";
const webinar_id = payload.webinar_id || payload.id || "";
const duration_attended = parseInt(payload.duration || payload.attendance_duration || "0");
const total_duration = parseInt(payload.webinar_duration || payload.total_duration || "60");
const polls_answered = parseInt(payload.polls_answered || payload.poll_count || "0");
const questions_asked = parseInt(payload.questions_asked || payload.qa_count || "0");
const chat_messages = parseInt(payload.chat_count || payload.messages_sent || "0");
const attendance_pct = total_duration > 0 ? Math.round((duration_attended / total_duration) * 100) : 0;
let engagement_score = 0;
engagement_score += Math.min(40, attendance_pct * 0.4);
engagement_score += polls_answered * 10;
engagement_score += questions_asked * 15;
engagement_score += chat_messages * 5;
engagement_score = Math.min(100, Math.round(engagement_score));
let attendee_segment = "passive";
if (engagement_score >= 70) attendee_segment = "highly_engaged";
else if (engagement_score >= 40) attendee_segment = "moderately_engaged";
const is_highly_engaged = attendee_segment === "highly_engaged" ? "true" : "false";
return {
  attendee_email: attendee_email,
  attendee_name: attendee_name,
  webinar_title: webinar_title,
  webinar_id: webinar_id,
  duration_attended: String(duration_attended),
  total_duration: String(total_duration),
  attendance_pct: String(attendance_pct),
  polls_answered: String(polls_answered),
  questions_asked: String(questions_asked),
  chat_messages: String(chat_messages),
  engagement_score: String(engagement_score),
  attendee_segment: attendee_segment,
  is_highly_engaged: is_highly_engaged
};`,
          outputVariables: [
            { name: "attendee_email", type: "string" },
            { name: "attendee_name", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "webinar_id", type: "string" },
            { name: "duration_attended", type: "string" },
            { name: "total_duration", type: "string" },
            { name: "attendance_pct", type: "string" },
            { name: "polls_answered", type: "string" },
            { name: "questions_asked", type: "string" },
            { name: "chat_messages", type: "string" },
            { name: "engagement_score", type: "string" },
            { name: "attendee_segment", type: "string" },
            { name: "is_highly_engaged", type: "string" },
          ],
        },
      }),

      // 3. CRM activity logging HubSpot
      hubspotPostNode(
        "3",
        "Log Webinar Activity to HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{attendee_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "attendee_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. Segment-based routing condition
      n("4", "condition", "Is Attendee Highly Engaged?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_highly_engaged", operator: "eq", value: "true" },
          ],
        },
      }),

      // 5. Day 1 recording next step email
      emailNode(
        "5",
        "Day 1: Recording and Next Steps Email",
        1000,
        "{attendee_email}",
        "Your recording is ready: {webinar_title}",
        "<p>Hi {attendee_name},</p><p>Thank you for attending <strong>{webinar_title}</strong>! You were there for {duration_attended} minutes and your engagement was great.</p><p><strong>Here is your recording:</strong><br/><a href='https://yourcompany.com/webinars/{webinar_id}/recording'>Watch the Full Recording</a></p><p><strong>Key Takeaways:</strong></p><ul><li>The slides and supplementary materials from the session</li><li>Answers to all Q&A questions (including ones we could not get to live)</li><li>Links to resources mentioned during the presentation</li></ul><p><strong>Your Next Step:</strong><br/>Based on what was covered, the best next move for teams like yours at {attendee_company} is to explore how these concepts apply to your specific situation.</p><p><a href='https://calendly.com/your-team/webinar-followup?email={attendee_email}'>Schedule a 15-minute strategy session</a> to discuss how to implement what you learned.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 2 days
      n("6", "delayAction", "Wait 2 Days Before Deeper Dive", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 2,
          delayUnit: "days",
          description:
            "Wait 2 days after the recording email to allow time for the attendee to watch the replay before sending deeper content",
        },
      }),

      // 7. Day 3 deeper dive email
      emailNode(
        "7",
        "Day 3: Deeper Dive Content Email",
        1480,
        "{attendee_email}",
        "{attendee_name}, going deeper on what we covered in {webinar_title}",
        "<p>Hi {attendee_name},</p><p>Hope you had a chance to review the recording from <strong>{webinar_title}</strong>.</p><p>We put together some deeper resources that expand on the most-discussed topics:</p><ul><li><strong>Deep Dive Guide:</strong> A comprehensive breakdown of the strategies covered, with step-by-step implementation instructions</li><li><strong>Benchmark Data:</strong> How companies in {attendee_company}'s space are performing against the metrics we discussed</li><li><strong>Template Pack:</strong> Ready-to-use templates based on the frameworks from the webinar</li></ul><p>These resources are specifically curated for attendees who showed strong engagement (like you — engagement score: {engagement_score}/100).</p><p>Which resource would be most valuable for your team? Reply and we will prioritize getting it to you.</p><p>Best,<br/>The Team</p>",
      ),

      // 8. Day 7 final CTA social proof email
      emailNode(
        "8",
        "Day 7: Final CTA with Social Proof",
        1720,
        "{attendee_email}",
        "Teams like {attendee_company} are already seeing results, {attendee_name}",
        '<p>Hi {attendee_name},</p><p>Since attending <strong>{webinar_title}</strong>, several attendees have already taken the next step and here is what they are saying:</p><blockquote><em>"We implemented the framework from the webinar and saw a 40% improvement in our first month."</em> — VP of Ops, similar-sized company to {attendee_company}</blockquote><blockquote><em>"The strategy session after the webinar was the most productive 15 minutes I have spent this quarter."</em> — Director, SaaS company</blockquote><p>The webinar covered the what — now it is time for the how. In a quick strategy call, we will map out:</p><ul><li>Which concepts from the webinar apply most to {attendee_company}</li><li>A 30-60-90 day implementation roadmap</li><li>Quick wins you can start with this week</li></ul><p><a href=\'https://calendly.com/your-team/strategy?email={attendee_email}\'>Claim your strategy session</a> — we are setting aside time specifically for webinar attendees this week.</p><p>Best,<br/>The Team</p>',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1694. WEBINAR ATTENDEE FOLLOW-UP (7 nodes)
  // ===================================================================

  "Webinar Attendee Follow-Up": {
    summary:
      "Fires when a webinar ends via webhook, enriches attendee data via Clearbit API, matches and updates the CRM contact in HubSpot, generates AI follow-up email content, sends a personalized follow-up email with resources, alerts SDRs on Slack for hot attendees, and logs engagement in HubSpot.",
    nodes: [
      trigger("Webhook: Webinar concluded with attendee list", "webhook_app", {
        source: "zoom",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar session concludes and attendee data becomes available — including each attendee's email, name, attendance duration, and engagement metrics — to trigger personalized follow-up for each participant",
        setup:
          "Configure Zoom webhook for webinar.ended events. Set Clearbit API key for attendee enrichment. Provide HubSpot token for CRM updates. Set OpenAI API key for AI email generation. Configure Slack webhook for SDR alerts.",
        inputFields: [],
      }),

      // 2. Attendee data enrichment Clearbit API
      n("2", "runCode", "Extract Attendee Data from Webhook", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract attendee data from the webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.attendee_email || payload.email || payload.participant?.email || "";
const name = payload.attendee_name || payload.name || payload.participant?.name || "";
const webinar_title = payload.webinar_title || payload.topic || "";
const duration = parseInt(payload.duration || payload.attendance_duration || "0");
const totalDuration = parseInt(payload.webinar_duration || "60");
const attendancePct = totalDuration > 0 ? Math.round((duration / totalDuration) * 100) : 0;
const questions = parseInt(payload.questions_asked || "0");
const polls = parseInt(payload.polls_answered || "0");
const isHot = (attendancePct >= 80 && (questions > 0 || polls >= 2)) ? "true" : "false";
return {
  attendee_email: email,
  attendee_name: name,
  webinar_title: webinar_title,
  attendance_duration: String(duration),
  attendance_pct: String(attendancePct),
  questions_asked: String(questions),
  polls_answered: String(polls),
  is_hot_attendee: isHot
};`,
          outputVariables: [
            { name: "attendee_email", type: "string" },
            { name: "attendee_name", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "attendance_duration", type: "string" },
            { name: "attendance_pct", type: "string" },
            { name: "questions_asked", type: "string" },
            { name: "polls_answered", type: "string" },
            { name: "is_hot_attendee", type: "string" },
          ],
        },
      }),

      // 3. Clearbit enrichment
      n("3", "hitApi", "Enrich Attendee via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={attendee_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 4. CRM contact match update HubSpot
      hubspotPostNode(
        "4",
        "Update CRM Contact in HubSpot",
        760,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{attendee_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","company","lifecyclestage","hubspot_owner_id"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "contact_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 5. AI follow-up email generator
      aiNode(
        "5",
        "AI Generate Personalized Follow-Up Email",
        1000,
        "You are a webinar follow-up email specialist. Given the attendee data including their role, company, industry, attendance metrics, and engagement level, craft a personalized follow-up email that references specific aspects of the webinar relevant to their industry and role. Include the recording link, curated resources, and a soft CTA. Respond JSON: {email_subject: string, email_body_html: string, recommended_resources: string, personalization_angle: string}",
        "Attendee: {attendee_name} ({enriched_title} at {enriched_company}). Industry: {company_industry}. Company size: {company_size}. Webinar: {webinar_title}. Attendance: {attendance_pct}% ({attendance_duration} min). Questions asked: {questions_asked}. Polls: {polls_answered}. Hot attendee: {is_hot_attendee}. Generate personalized follow-up email.",
        [
          { field: "email_subject", as: "followup_subject", type: "string" },
          { field: "email_body_html", as: "followup_body", type: "string" },
          {
            field: "recommended_resources",
            as: "recommended_resources",
            type: "string",
          },
          {
            field: "personalization_angle",
            as: "personalization_angle",
            type: "string",
          },
        ],
      ),

      // 6. Follow-up email with resources
      emailNode(
        "6",
        "Send Personalized Follow-Up Email",
        1240,
        "{attendee_email}",
        "{followup_subject}",
        "{followup_body}",
      ),

      // 7. Hot attendee SDR alert Slack
      slackNode(
        "7",
        "Alert SDR for Hot Attendee",
        1480,
        "Hot Webinar Attendee Alert\\n*{attendee_name}* — {enriched_title} at {enriched_company}\\nWebinar: {webinar_title}\\nAttendance: {attendance_pct}% | Questions: {questions_asked} | Polls: {polls_answered}\\nIndustry: {company_industry} | Size: {company_size} employees\\nPersonalization angle: {personalization_angle}\\nRecommended resources: {recommended_resources}\\nHubSpot: https://app.hubspot.com/contacts/{hs_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1695. WEBINAR ATTENDEE NON-CONVERTER NURTURE (8 nodes)
  // ===================================================================

  "Webinar Attendee Non-Converter Nurture": {
    summary:
      "Runs on a schedule 7 days post-webinar, analyzes engagement history from HubSpot, generates AI barrier analysis, routes by segment via condition, sends a Week 2 barrier-specific content email, a Week 3 personalized sales touch email, a Week 4 social proof soft CTA email, and checks re-qualification via condition.",
    nodes: [
      trigger("Schedule: 7 days post-webinar non-converter check", "schedule", {
        schedule: "Daily at 9:00 AM",
        howItStarts:
          "Runs daily to identify webinar attendees who attended 7+ days ago but have not converted — have not booked a demo, started a trial, or replied to follow-up — to enroll them into an extended nurture sequence addressing specific conversion barriers",
        setup:
          "Configure HubSpot API token for engagement history lookups. Set OpenAI API key for barrier analysis. Configure email sending credentials for the multi-week nurture sequence.",
        inputFields: [],
      }),

      // 2. Engagement history analysis HubSpot
      hubspotGetNode(
        "2",
        "Analyze Engagement History from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_click_date",
            customName: "last_email_click",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contact_notes",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "page_views",
            type: "string",
          },
        ],
      ),

      // 3. AI barrier analysis
      aiNode(
        "3",
        "AI Analyze Conversion Barriers",
        520,
        "You are a conversion optimization specialist. Given a webinar attendee who has not converted after 7 days, analyze their engagement signals (email opens, clicks, page views, notes) to determine the most likely conversion barrier. Common barriers: unclear ROI, no urgency, too complex, wrong timing, needs social proof, budget concerns, needs internal buy-in. Respond JSON: {primary_barrier: string, barrier_explanation: string, recommended_content: string, personalized_hook: string, urgency_trigger: string, social_proof_angle: string, is_recoverable: string}",
        "Contact: {contact_name} ({contact_title} at {contact_company}). Last email open: {last_email_open}. Last click: {last_email_click}. Page views: {page_views}. Notes: {last_contact_notes}. This person attended a webinar 7+ days ago but has not converted. Diagnose the barrier.",
        [
          { field: "primary_barrier", as: "primary_barrier", type: "string" },
          {
            field: "barrier_explanation",
            as: "barrier_explanation",
            type: "string",
          },
          {
            field: "recommended_content",
            as: "recommended_content",
            type: "string",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
          { field: "urgency_trigger", as: "urgency_trigger", type: "string" },
          {
            field: "social_proof_angle",
            as: "social_proof_angle",
            type: "string",
          },
          { field: "is_recoverable", as: "is_recoverable", type: "string" },
        ],
      ),

      // 4. Segment routing condition
      n("4", "condition", "Is Lead Recoverable?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_recoverable", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. Week 2 barrier-specific content email
      emailNode(
        "5",
        "Week 2: Barrier-Specific Content Email",
        1000,
        "{contact_email}",
        "{contact_name}, addressing what might be holding you back",
        "<p>Hi {contact_name},</p><p>We noticed you attended our webinar recently and wanted to follow up with something specific to your situation at {contact_company}.</p><p>{personalized_hook}</p><p><strong>We put together this resource specifically for teams facing {primary_barrier}:</strong></p><p>{recommended_content}</p><p>The key insight: {barrier_explanation}</p><p>Many teams in your position have found that the biggest unlock was simply seeing how it works in their specific context. No commitment needed — just clarity.</p><p>Would a 10-minute walkthrough tailored to {contact_company} be useful?</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Week 3 personalized sales touch email
      emailNode(
        "6",
        "Week 3: Personalized Sales Touch Email",
        1240,
        "{contact_email}",
        "Quick question for you, {contact_name}",
        '<p>Hi {contact_name},</p><p>I will keep this short. After reviewing your engagement with our content and understanding what {contact_company} is working on, I had one question:</p><p><strong>{urgency_trigger}</strong></p><p>The reason I ask is that teams in the {contact_title} role at companies like yours often tell us their biggest challenge is {primary_barrier}. We have helped dozens of similar teams solve exactly that.</p><p>If this resonates, I would love to share what has worked for others — no pitch, just insights from teams who were in your exact situation.</p><p>A quick reply with "yes" or "not now" would be great. I respect your time either way.</p><p>Best,<br/>The Team</p>',
      ),

      // 7. Week 4 social proof soft CTA email
      emailNode(
        "7",
        "Week 4: Social Proof Soft CTA Email",
        1480,
        "{contact_email}",
        "{contact_name}, here is what teams like {contact_company} are saying",
        "<p>Hi {contact_name},</p><p>{social_proof_angle}</p><p><strong>Results from teams similar to yours:</strong></p><ul><li>45% reduction in manual processes within 60 days</li><li>3x faster execution on key workflows</li><li>ROI positive within the first quarter</li></ul><p>These are not outliers — they are the norm for teams that made the switch.</p><p>If you are heading into a new planning cycle at {contact_company}, this might be the perfect time to explore how these results could apply to your team.</p><p><a href='https://calendly.com/your-team/demo?email={contact_email}'>Grab 15 minutes on my calendar</a> — I will show you exactly how teams like yours got started.</p><p>Best,<br/>The Team</p>",
      ),

      // 8. Re-qualification decision condition
      n("8", "condition", "Has Contact Engaged with Nurture?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "last_email_click", operator: "neq", value: "" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1696. WEBINAR DAY-BEFORE REMINDER (6 nodes)
  // ===================================================================

  "Webinar Day-Before Reminder": {
    summary:
      "Runs on a schedule 24 hours before the webinar, fetches the registrant list via API, formats timezone-aware times with runCode, sends a reminder email with agenda, updates the Google Calendar event via API, and handles delivery failures via condition.",
    nodes: [
      trigger("Schedule: 24 hours before webinar", "schedule", {
        schedule: "24 hours before webinar start time",
        howItStarts:
          "Runs exactly 24 hours before a scheduled webinar to send day-before reminders to all registrants with agenda details, join links, and timezone-appropriate timing information",
        setup:
          "Configure schedule trigger for 24 hours before each webinar. Set webinar platform API credentials for registrant list retrieval. Configure email sending credentials. Set Google Calendar API credentials for event updates.",
        inputFields: [],
      }),

      // 2. Registrant list fetch API
      n("2", "hitApi", "Fetch Registrant List from Webinar Platform", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/webinars/{webinar_id}/registrants?status=approved&page_size=100",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_jwt_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "registrants[0].email",
              customName: "registrant_email",
              type: "string",
            },
            {
              name: "registrants[0].first_name",
              customName: "registrant_name",
              type: "string",
            },
            {
              name: "registrants[0].join_url",
              customName: "join_url",
              type: "string",
            },
            {
              name: "total_records",
              customName: "total_registrants",
              type: "string",
            },
            {
              name: "registrants[0].create_time",
              customName: "registration_date",
              type: "string",
            },
          ],
        },
      }),

      // 3. Timezone-aware time formatting runCode
      n("3", "runCode", "Format Timezone-Aware Webinar Times", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format webinar time for common timezones
const webinarStart = new Date();
webinarStart.setDate(webinarStart.getDate() + 1);
webinarStart.setHours(11, 0, 0, 0); // Default 11 AM ET
const etTime = webinarStart.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
const ptTime = webinarStart.toLocaleString("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
const gmtTime = webinarStart.toLocaleString("en-US", { timeZone: "Europe/London", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
const dateStr = webinarStart.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
return {
  formatted_date: dateStr,
  time_et: etTime,
  time_pt: ptTime,
  time_gmt: gmtTime,
  time_summary: etTime + " / " + ptTime + " / " + gmtTime,
  total_registrants: vars.total_registrants || "0",
  is_delivery_successful: "true"
};`,
          outputVariables: [
            { name: "formatted_date", type: "string" },
            { name: "time_et", type: "string" },
            { name: "time_pt", type: "string" },
            { name: "time_gmt", type: "string" },
            { name: "time_summary", type: "string" },
            { name: "total_registrants", type: "string" },
            { name: "is_delivery_successful", type: "string" },
          ],
        },
      }),

      // 4. Reminder email with agenda
      emailNode(
        "4",
        "Send Day-Before Reminder Email",
        760,
        "{registrant_email}",
        "Tomorrow: Your webinar starts at {time_et} — here is everything you need",
        "<p>Hi {registrant_name},</p><p>Just a friendly reminder — the webinar you registered for is <strong>tomorrow</strong>!</p><p><strong>When:</strong> {formatted_date}<br/>{time_summary}</p><p><strong>Your Personal Join Link:</strong><br/><a href='{join_url}'>Click here to join the webinar</a></p><p><strong>Agenda:</strong></p><ol><li><strong>Welcome and Introduction</strong> (5 min)</li><li><strong>Core Presentation</strong> (25 min) — The main content you signed up for</li><li><strong>Live Demo</strong> (15 min) — See concepts in action</li><li><strong>Q&A Session</strong> (15 min) — Your questions answered live</li></ol><p><strong>Quick Tips:</strong></p><ul><li>Join 2-3 minutes early to test your audio</li><li>Prepare any questions you want to ask during Q&A</li><li>The session will be recorded, but attending live gets you exclusive bonus materials</li></ul><p>See you tomorrow!</p><p>The Team</p>",
      ),

      // 5. Calendar event update Google Calendar API
      n("5", "hitApi", "Update Google Calendar Event", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events/{calendar_event_id}",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "reminders",
              value:
                '{"useDefault":false,"overrides":[{"method":"popup","minutes":60},{"method":"email","minutes":30}]}',
            },
            {
              key: "description",
              value:
                "Webinar tomorrow at {time_summary}. Join: {join_url}. Total registrants: {total_registrants}.",
            },
          ],
          variables: [
            {
              name: "status",
              customName: "calendar_update_status",
              type: "string",
            },
            {
              name: "updated",
              customName: "calendar_updated_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Delivery failure handler condition
      n("6", "condition", "Was Delivery Successful?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "is_delivery_successful",
              operator: "eq",
              value: "true",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1697. WEBINAR HOUR-BEFORE REMINDER (5 nodes)
  // ===================================================================

  "Webinar Hour-Before Reminder": {
    summary:
      "Runs on a schedule 1 hour before the webinar, filters active registrants via API, sends a final reminder email, sends an SMS reminder via Twilio, and logs pre-join analytics to Google Sheets.",
    nodes: [
      trigger("Schedule: 1 hour before webinar", "schedule", {
        schedule: "1 hour before webinar start time",
        howItStarts:
          "Runs exactly 1 hour before a scheduled webinar to send final reminders to registrants who have not yet joined — via both email and SMS — to maximize live attendance",
        setup:
          "Configure schedule trigger for 1 hour before each webinar. Set webinar platform API credentials for registrant filtering. Configure email and SMS (Twilio) sending credentials. Set Google Sheets for analytics.",
        inputFields: [],
      }),

      // 2. Active registrant filter API
      n("2", "hitApi", "Filter Active Registrants from Platform", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/webinars/{webinar_id}/registrants?status=approved&page_size=100",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_jwt_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "registrants[0].email",
              customName: "registrant_email",
              type: "string",
            },
            {
              name: "registrants[0].first_name",
              customName: "registrant_name",
              type: "string",
            },
            {
              name: "registrants[0].phone",
              customName: "registrant_phone",
              type: "string",
            },
            {
              name: "registrants[0].join_url",
              customName: "join_url",
              type: "string",
            },
            {
              name: "total_records",
              customName: "total_registrants",
              type: "string",
            },
          ],
        },
      }),

      // 3. Final reminder email
      emailNode(
        "3",
        "Send Final Reminder Email",
        520,
        "{registrant_email}",
        "Starting in 1 hour — join link inside",
        "<p>Hi {registrant_name},</p><p>The webinar starts in <strong>1 hour</strong>!</p><p><a href='{join_url}'><strong>Click here to join the webinar</strong></a></p><p>Quick tips:</p><ul><li>Join 2-3 minutes early to test your audio and video</li><li>Have questions ready for the live Q&A</li><li>Close unnecessary tabs for the best experience</li></ul><p>See you shortly!</p><p>The Team</p>",
      ),

      // 4. SMS reminder Twilio
      smsNode(
        "4",
        "Send SMS Reminder via Twilio",
        760,
        "{registrant_phone}",
        "Hi {registrant_name}! Your webinar starts in 1 hour. Join here: {join_url}",
      ),

      // 5. Pre-join analytics Sheets
      sheetsNode(
        "5",
        "Log Pre-Join Analytics to Sheets",
        1000,
        "WebinarReminders",
        '[["{registrant_name}","{registrant_email}","{registrant_phone}","{total_registrants}","1hr_reminder","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
  },

  // ===================================================================
  // 1698. WEBINAR INVITE SERIES (8 nodes)
  // ===================================================================

  "Webinar Invite Series": {
    summary:
      "Fires when a webinar is created via webhook, segments the audience from HubSpot, generates AI invite copy, sends a Week 1 primary invitation email, checks registration via condition, sends a Week 2 reminder to non-registrants, sends a Week 3 final push email, and sends registration confirmation email.",
    nodes: [
      trigger("Webhook: New webinar created", "webhook_app", {
        source: "zoom",
        event: "webinar.created",
        howItStarts:
          "Fires when a new webinar is created on the platform — including the title, description, date, time, duration, and speaker information — to automatically launch a multi-week invitation campaign to the target audience",
        setup:
          "Configure Zoom webhook for webinar.created events. Set HubSpot API token for audience segmentation. Set OpenAI API key for AI copy generation. Configure email sending credentials for the invitation series.",
        inputFields: [],
      }),

      // 2. AI extract webinar details
      aiNode(
        "2",
        "AI Extract Webinar Details",
        280,
        "You are a webinar event parser. Extract the webinar title, description, date, start time, duration, timezone, speaker names, speaker bios, registration URL, webinar ID, and target audience from the raw webhook payload. Respond JSON: {webinar_title: string, webinar_description: string, webinar_date: string, start_time: string, duration_minutes: string, timezone: string, speaker_names: string, speaker_bios: string, registration_url: string, webinar_id: string, target_audience: string}",
        "Raw webhook payload: {webhook_payload}. Extract all webinar creation data regardless of field naming conventions.",
        [
          { field: "webinar_title", as: "webinar_title", type: "string" },
          {
            field: "webinar_description",
            as: "webinar_description",
            type: "string",
          },
          { field: "webinar_date", as: "webinar_date", type: "string" },
          { field: "start_time", as: "start_time", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "string" },
          { field: "timezone", as: "timezone", type: "string" },
          { field: "speaker_names", as: "speaker_names", type: "string" },
          { field: "speaker_bios", as: "speaker_bios", type: "string" },
          { field: "registration_url", as: "registration_url", type: "string" },
          { field: "webinar_id", as: "webinar_id", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
        ],
      ),

      // 3. Audience segmentation HubSpot
      hubspotGetNode(
        "3",
        "Segment Target Audience from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "contact_industry",
            type: "string",
          },
          { name: "total", customName: "total_audience_size", type: "string" },
        ],
      ),

      // 4. AI invite copy generator
      aiNode(
        "4",
        "AI Generate Invite Copy",
        760,
        "You are an expert webinar marketing copywriter. Given the webinar details, speaker information, and target audience profile, create compelling invitation copy for a 3-week email series. Write engaging subject lines and bodies for each week: Week 1 (primary invite), Week 2 (reminder for non-registrants), and Week 3 (final push with urgency). Respond JSON: {week1_subject: string, week1_body_html: string, week2_subject: string, week2_body_html: string, week3_subject: string, week3_body_html: string, value_proposition: string, urgency_hook: string}",
        "Webinar: {webinar_title}. Description: {webinar_description}. Date: {webinar_date} at {start_time} {timezone}. Duration: {duration_minutes} min. Speakers: {speaker_names}. Speaker bios: {speaker_bios}. Registration URL: {registration_url}. Contact: {contact_name} ({contact_title} at {contact_company}, {contact_industry}). Audience size: {total_audience_size}. Generate the invitation series.",
        [
          { field: "week1_subject", as: "week1_subject", type: "string" },
          { field: "week1_body_html", as: "week1_body", type: "string" },
          { field: "week2_subject", as: "week2_subject", type: "string" },
          { field: "week2_body_html", as: "week2_body", type: "string" },
          { field: "week3_subject", as: "week3_subject", type: "string" },
          { field: "week3_body_html", as: "week3_body", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),

      // 5. Week 1 primary invitation email
      emailNode(
        "5",
        "Week 1: Primary Invitation Email",
        1000,
        "{contact_email}",
        "{week1_subject}",
        "{week1_body}",
      ),

      // 6. Registration tracking condition
      n("6", "condition", "Has Contact Registered?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "contact_email", operator: "neq", value: "" },
          ],
        },
      }),

      // 7. Week 2 reminder to non-registrants email
      emailNode(
        "7",
        "Week 2: Reminder for Non-Registrants",
        1480,
        "{contact_email}",
        "{week2_subject}",
        "{week2_body}",
      ),

      // 8. Week 3 final push email
      emailNode(
        "8",
        "Week 3: Final Push with Urgency",
        1720,
        "{contact_email}",
        "{week3_subject}",
        "{week3_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1699. WEBINAR NO-SHOW FOLLOW-UP (7 nodes)
  // ===================================================================

  "Webinar No-Show Follow-Up": {
    summary:
      "Fires when a webinar ends via webhook, identifies no-shows with runCode, logs no-show data in HubSpot CRM, generates AI empathetic follow-up content, sends a no-show email with recording link, monitors engagement via condition, and routes re-engagement via condition.",
    nodes: [
      trigger("Webhook: Webinar ended with attendance data", "webhook_app", {
        source: "zoom",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar concludes and attendance data is available — to identify registrants who did not attend and send empathetic, non-judgmental follow-up with the recording and alternative ways to engage",
        setup:
          "Configure Zoom webhook for webinar.ended events. Set OpenAI API key for empathetic copy generation. Provide HubSpot token for no-show logging. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. No-show identification runCode
      n("2", "runCode", "Identify No-Show Registrants", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook to identify no-show registrants
const payload = JSON.parse(vars.webhook_payload || "{}");
const registrants = payload.registrants || payload.all_registrants || [];
const attendees = payload.attendees || payload.participants || [];
const webinar_title = payload.topic || payload.webinar_title || "";
const webinar_id = payload.id || payload.webinar_id || "";
const recording_url = payload.recording_url || payload.share_url || "";
const attendeeEmails = new Set((Array.isArray(attendees) ? attendees : []).map(a => (a.email || "").toLowerCase()));
let noShowEmail = "";
let noShowName = "";
let noShowCompany = "";
let totalRegistrants = 0;
let totalNoShows = 0;
if (Array.isArray(registrants)) {
  totalRegistrants = registrants.length;
  for (const r of registrants) {
    const email = (r.email || "").toLowerCase();
    if (email && !attendeeEmails.has(email)) {
      if (!noShowEmail) {
        noShowEmail = email;
        noShowName = r.first_name || r.name || "";
        noShowCompany = r.company || r.organization || "";
      }
      totalNoShows++;
    }
  }
}
const noShowRate = totalRegistrants > 0 ? Math.round((totalNoShows / totalRegistrants) * 100) : 0;
const has_recording = recording_url ? "true" : "false";
return {
  noshow_email: noShowEmail,
  noshow_name: noShowName,
  noshow_company: noShowCompany,
  webinar_title: webinar_title,
  webinar_id: webinar_id,
  recording_url: recording_url,
  total_registrants: String(totalRegistrants),
  total_noshows: String(totalNoShows),
  noshow_rate: String(noShowRate),
  has_recording: has_recording
};`,
          outputVariables: [
            { name: "noshow_email", type: "string" },
            { name: "noshow_name", type: "string" },
            { name: "noshow_company", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "webinar_id", type: "string" },
            { name: "recording_url", type: "string" },
            { name: "total_registrants", type: "string" },
            { name: "total_noshows", type: "string" },
            { name: "noshow_rate", type: "string" },
            { name: "has_recording", type: "string" },
          ],
        },
      }),

      // 3. CRM no-show logging HubSpot
      hubspotPostNode(
        "3",
        "Log No-Show in HubSpot CRM",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{noshow_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","company","lifecyclestage"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. AI empathetic follow-up
      aiNode(
        "4",
        "AI Generate Empathetic No-Show Follow-Up",
        760,
        "You are a thoughtful marketing communications specialist. Craft an empathetic, non-judgmental follow-up email for someone who registered for a webinar but did not attend. Never guilt-trip or make them feel bad. Acknowledge that life gets busy, offer the recording, and provide alternative ways to get the same value. Respond JSON: {email_subject: string, email_body_html: string, alternative_cta: string, re_engagement_hook: string}",
        "No-show: {noshow_name} from {noshow_company}. Webinar: {webinar_title}. Recording available: {has_recording}. Recording URL: {recording_url}. Generate an empathetic, value-first follow-up.",
        [
          { field: "email_subject", as: "noshow_subject", type: "string" },
          { field: "email_body_html", as: "noshow_body", type: "string" },
          { field: "alternative_cta", as: "alternative_cta", type: "string" },
          {
            field: "re_engagement_hook",
            as: "re_engagement_hook",
            type: "string",
          },
        ],
      ),

      // 5. No-show email with recording
      emailNode(
        "5",
        "Send No-Show Email with Recording",
        1000,
        "{noshow_email}",
        "{noshow_subject}",
        "{noshow_body}",
      ),

      // 6. Engagement monitoring condition
      n("6", "condition", "Is Recording Available?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "has_recording", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Re-engagement routing condition
      n("7", "condition", "Was No-Show Rate Above 40%?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "noshow_rate", operator: "gte", value: "40" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1700. WEBINAR NO-SHOW REPLAY SEQUENCE (8 nodes)
  // ===================================================================

  "Webinar No-Show Replay Sequence": {
    summary:
      "Fires when a webinar ends via webhook, profiles no-shows from HubSpot, sends a Day 1 replay available email, waits 2 days, sends a Day 3 key takeaways email, sends a Day 5 speaker insights email, checks replay view tracking via condition, and sends a Day 7 CTA based on engagement email.",
    nodes: [
      trigger("Webhook: Webinar ended for replay distribution", "webhook_app", {
        source: "zoom",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar concludes to launch a multi-day replay sequence for no-show registrants — delivering value through the recording, key takeaways, and speaker insights to recover engagement and drive conversions",
        setup:
          "Configure Zoom webhook for webinar.ended events. Provide HubSpot token for no-show profiling. Configure email sending credentials for the 7-day replay sequence.",
        inputFields: [],
      }),

      // 2. No-show profiling + data extraction
      n("2", "runCode", "Profile No-Show Registrants", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract no-show data and webinar details for replay sequence
const payload = JSON.parse(vars.webhook_payload || "{}");
const noshow_email = payload.noshow_email || payload.email || payload.registrant?.email || "";
const noshow_name = payload.noshow_name || payload.name || payload.registrant?.first_name || "";
const webinar_title = payload.topic || payload.webinar_title || "";
const recording_url = payload.recording_url || payload.share_url || "";
const webinar_duration = payload.duration || "60";
const speaker_names = payload.speakers || payload.host_name || "";
const key_topics = payload.agenda || payload.description || "";
return {
  noshow_email: noshow_email,
  noshow_name: noshow_name,
  webinar_title: webinar_title,
  recording_url: recording_url,
  webinar_duration: webinar_duration,
  speaker_names: speaker_names,
  key_topics: key_topics,
  has_viewed_replay: "false"
};`,
          outputVariables: [
            { name: "noshow_email", type: "string" },
            { name: "noshow_name", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "recording_url", type: "string" },
            { name: "webinar_duration", type: "string" },
            { name: "speaker_names", type: "string" },
            { name: "key_topics", type: "string" },
            { name: "has_viewed_replay", type: "string" },
          ],
        },
      }),

      // 3. Day 1 replay available email
      emailNode(
        "3",
        "Day 1: Replay Available Email",
        520,
        "{noshow_email}",
        "You missed it live — here is the full recording of {webinar_title}",
        "<p>Hi {noshow_name},</p><p>We missed you at <strong>{webinar_title}</strong>, but no worries — we recorded the entire session so you will not miss a thing.</p><p><a href='{recording_url}'><strong>Watch the Full Recording ({webinar_duration} min)</strong></a></p><p>Here is what was covered:</p><ul><li>{key_topics}</li></ul><p>Featuring: {speaker_names}</p><p>The recording is available for the next 14 days, so watch at your convenience. Pro tip: watch at 1.5x speed if you are short on time — the key insights start around the 5-minute mark.</p><p>Enjoy!</p><p>The Team</p>",
      ),

      // 4. Delay 2 days
      n("4", "delayAction", "Wait 2 Days Before Key Takeaways", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 2,
          delayUnit: "days",
          description:
            "Wait 2 days after the replay email to give the no-show time to watch before sending key takeaways",
        },
      }),

      // 5. Day 3 key takeaways email
      emailNode(
        "5",
        "Day 3: Key Takeaways Email",
        1000,
        "{noshow_email}",
        "The 5 biggest takeaways from {webinar_title}",
        "<p>Hi {noshow_name},</p><p>Whether or not you have had a chance to watch the recording yet, here are the 5 biggest takeaways from <strong>{webinar_title}</strong>:</p><ol><li><strong>Takeaway #1:</strong> The current landscape is shifting faster than most teams realize — early movers are seeing 3x better outcomes</li><li><strong>Takeaway #2:</strong> The #1 mistake teams make is waiting for the perfect time — incremental progress beats delayed perfection</li><li><strong>Takeaway #3:</strong> Automation is no longer optional — teams that automate see 40% efficiency gains within 90 days</li><li><strong>Takeaway #4:</strong> Data-driven decision making starts with the right measurement framework (we shared ours)</li><li><strong>Takeaway #5:</strong> The best-performing teams all share one trait — they act fast on insights</li></ol><p>Want the full context? <a href='{recording_url}'>Watch the recording</a> — the Q&A at the end was especially insightful.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Day 5 speaker insights email
      emailNode(
        "6",
        "Day 5: Speaker Insights Email",
        1240,
        "{noshow_email}",
        "Exclusive insights from {speaker_names} after {webinar_title}",
        "<p>Hi {noshow_name},</p><p>After <strong>{webinar_title}</strong>, we sat down with {speaker_names} to dig deeper into the questions that came up during the live session.</p><p><strong>Top questions from the audience:</strong></p><ul><li>How do you get started without disrupting current workflows?</li><li>What is the realistic timeline to see ROI?</li><li>How do you get buy-in from leadership?</li></ul><p><strong>{speaker_names} had some fascinating answers:</strong></p><p>The key insight was that most teams overthink the starting point. The best approach is to pick one high-impact workflow, prove value in 30 days, and let the results build the case for broader adoption.</p><p>Full recording with all Q&A: <a href='{recording_url}'>Watch here</a></p><p>Best,<br/>The Team</p>",
      ),

      // 7. Replay view tracking condition
      n("7", "condition", "Has No-Show Viewed the Replay?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "has_viewed_replay", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Day 7 CTA based on engagement email
      emailNode(
        "8",
        "Day 7: CTA Based on Engagement Email",
        1720,
        "{noshow_email}",
        "{noshow_name}, ready to put {webinar_title} insights into action?",
        "<p>Hi {noshow_name},</p><p>It has been a week since <strong>{webinar_title}</strong>, and the feedback from attendees has been incredible.</p><p><strong>What attendees are saying:</strong></p><blockquote><em>\"This was the most actionable webinar I have attended this year. We implemented two changes the same week.\"</em></blockquote><blockquote><em>\"The framework shared made something complex suddenly feel achievable.\"</em></blockquote><p>The recording is still available for a few more days: <a href='{recording_url}'>Watch now</a></p><p><strong>Ready to take the next step?</strong></p><p>Many attendees have already booked a strategy session to map the webinar insights to their specific situation. If you would like the same:</p><p><a href='https://calendly.com/your-team/webinar-followup?email={noshow_email}'>Book Your Strategy Session</a></p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1701. WEBINAR REGISTRATION CONFIRMATION (6 nodes)
  // ===================================================================

  "Webinar Registration Confirmation": {
    summary:
      "Fires on webinar registration via webhook, enriches the registrant via Clearbit API, updates the CRM record in HubSpot, sends a confirmation email, creates a Google Calendar event via API, and delivers sales team intelligence on Slack.",
    nodes: [
      trigger("Webhook: Webinar registration received", "webhook_app", {
        source: "zoom",
        event: "webinar.registration_created",
        howItStarts:
          "Fires when a new registrant signs up for a webinar — including their contact details, company information, and selected sessions — to trigger an instant confirmation and pre-event intelligence pipeline",
        setup:
          "Configure Zoom webhook for webinar.registration_created events. Set Clearbit API key for registrant enrichment. Provide HubSpot token for CRM updates. Configure Google Calendar API for event creation. Set Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. Registrant data enrichment Clearbit API
      n("2", "runCode", "Extract Registration Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract registration data from webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.registrant?.email || "";
const name = payload.first_name || payload.registrant?.first_name || "";
const lastName = payload.last_name || payload.registrant?.last_name || "";
const company = payload.company || payload.registrant?.org || "";
const title = payload.job_title || payload.registrant?.job_title || "";
const phone = payload.phone || payload.registrant?.phone || "";
const webinar_title = payload.topic || payload.webinar?.topic || "";
const webinar_date = payload.start_time || payload.webinar?.start_time || "";
const join_url = payload.join_url || payload.registrant?.join_url || "";
const webinar_id = payload.webinar_id || payload.id || "";
return {
  registrant_email: email,
  registrant_name: name,
  registrant_last_name: lastName,
  registrant_company: company,
  registrant_title: title,
  registrant_phone: phone,
  webinar_title: webinar_title,
  webinar_date: webinar_date,
  join_url: join_url,
  webinar_id: webinar_id
};`,
          outputVariables: [
            { name: "registrant_email", type: "string" },
            { name: "registrant_name", type: "string" },
            { name: "registrant_last_name", type: "string" },
            { name: "registrant_company", type: "string" },
            { name: "registrant_title", type: "string" },
            { name: "registrant_phone", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "webinar_date", type: "string" },
            { name: "join_url", type: "string" },
            { name: "webinar_id", type: "string" },
          ],
        },
      }),

      // 3. Clearbit enrichment
      n("3", "hitApi", "Enrich Registrant via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={registrant_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. CRM record update HubSpot
      hubspotPostNode(
        "4",
        "Update CRM Record in HubSpot",
        760,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{registrant_email}","firstname":"{registrant_name}","lastname":"{registrant_last_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","phone":"{registrant_phone}","industry":"{company_industry}","notes_last_contacted":"Registered for webinar: {webinar_title} on {webinar_date}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_updated_at",
            type: "string",
          },
        ],
      ),

      // 5. Confirmation email delivery
      emailNode(
        "5",
        "Send Webinar Confirmation Email",
        1000,
        "{registrant_email}",
        "You are registered for {webinar_title}, {registrant_name}!",
        "<p>Hi {registrant_name},</p><p>You are all set! Here are your webinar details:</p><p><strong>Webinar:</strong> {webinar_title}<br/><strong>Date:</strong> {webinar_date}<br/><strong>Your Join Link:</strong> <a href='{join_url}'>Click here to join</a></p><p><strong>What to Expect:</strong></p><ul><li>Expert-led presentation with actionable insights</li><li>Live Q&A — bring your toughest questions</li><li>Downloadable resources shared during the session</li><li>Recording available if you cannot make it live</li></ul><p><strong>To Get the Most Out of It:</strong></p><ol><li>Add it to your calendar (link below)</li><li>Join 2-3 minutes early</li><li>Prepare 1-2 questions for the Q&A</li></ol><p>See you there!</p><p>The Team</p>",
      ),

      // 6. Sales team intelligence Slack
      slackNode(
        "6",
        "Deliver Sales Intelligence to Slack",
        1240,
        "New Webinar Registration: *{webinar_title}*\\n*{registrant_name} {registrant_last_name}* — {enriched_title} at {enriched_company}\\nIndustry: {company_industry} | Size: {company_size} employees | Revenue: ${company_revenue}\\nEmail: {registrant_email} | Phone: {registrant_phone}\\nWebinar date: {webinar_date}\\nHubSpot: https://app.hubspot.com/contacts/{hs_contact_id}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1702. WEBINAR REGISTRATION NURTURE (7 nodes)
  // ===================================================================

  "Webinar Registration Nurture": {
    summary:
      "Fires on webinar registration via webhook, calculates time-to-event with runCode, sends a Week 1 speaker spotlight email, waits 7 days, sends a Week 2 pre-webinar content email, sends community engagement on Slack, and sends a 3-days-before what-to-expect email.",
    nodes: [
      trigger(
        "Webhook: Webinar registration for pre-event nurture",
        "webhook_app",
        {
          source: "zoom",
          event: "webinar.registration_created",
          howItStarts:
            "Fires when a new registrant signs up for an upcoming webinar to begin a pre-event nurture sequence that builds anticipation, introduces speakers, shares preparatory content, and maximizes live attendance",
          setup:
            "Configure Zoom webhook for registration events. Set OpenAI API key for content extraction. Configure email sending credentials. Set Slack webhook for community engagement notifications.",
          inputFields: [],
        },
      ),

      // 2. Time-to-event calculator runCode
      n("2", "runCode", "Calculate Time to Event and Extract Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract registration data and calculate time to event
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.registrant?.email || "";
const name = payload.first_name || payload.registrant?.first_name || "";
const company = payload.company || payload.registrant?.org || "";
const webinar_title = payload.topic || payload.webinar?.topic || "";
const webinar_date = payload.start_time || payload.webinar?.start_time || "";
const speaker = payload.host_name || payload.speakers || "";
const join_url = payload.join_url || payload.registrant?.join_url || "";
const now = new Date();
const eventDate = new Date(webinar_date || now);
const daysUntilEvent = Math.max(0, Math.round((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
const weeksUntilEvent = Math.round(daysUntilEvent / 7);
return {
  registrant_email: email,
  registrant_name: name,
  registrant_company: company,
  webinar_title: webinar_title,
  webinar_date: webinar_date,
  speaker_names: speaker,
  join_url: join_url,
  days_until_event: String(daysUntilEvent),
  weeks_until_event: String(weeksUntilEvent)
};`,
          outputVariables: [
            { name: "registrant_email", type: "string" },
            { name: "registrant_name", type: "string" },
            { name: "registrant_company", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "webinar_date", type: "string" },
            { name: "speaker_names", type: "string" },
            { name: "join_url", type: "string" },
            { name: "days_until_event", type: "string" },
            { name: "weeks_until_event", type: "string" },
          ],
        },
      }),

      // 3. Week 1 speaker spotlight email
      emailNode(
        "3",
        "Week 1: Speaker Spotlight Email",
        520,
        "{registrant_email}",
        "Meet your speakers for {webinar_title}, {registrant_name}",
        "<p>Hi {registrant_name},</p><p>Your webinar is {days_until_event} days away! We wanted to give you a sneak peek at who you will be learning from.</p><p><strong>Featured Speakers: {speaker_names}</strong></p><p>Our speakers bring deep expertise in the exact challenges that teams at {registrant_company} face daily. Here is what makes this session special:</p><ul><li><strong>Real-world experience:</strong> Not theory — proven strategies from practitioners</li><li><strong>Interactive format:</strong> Plenty of time for your specific questions</li><li><strong>Actionable frameworks:</strong> Walk away with something you can implement immediately</li></ul><p><strong>Prepare a question:</strong> The best webinars are interactive. Think about the #1 challenge your team faces right now and bring it to the Q&A.</p><p>Your join link: <a href='{join_url}'>Save this for the day of the event</a></p><p>See you in {days_until_event} days!</p><p>The Team</p>",
      ),

      // 4. Delay 7 days
      n("4", "delayAction", "Wait 7 Days Before Week 2 Content", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days between Week 1 speaker spotlight and Week 2 preparatory content email",
        },
      }),

      // 5. Week 2 pre-webinar content email
      emailNode(
        "5",
        "Week 2: Pre-Webinar Content Email",
        1000,
        "{registrant_email}",
        "Get ready for {webinar_title}: prep materials inside",
        "<p>Hi {registrant_name},</p><p>The webinar is coming up soon and we want to make sure you get maximum value from the session.</p><p><strong>Pre-Webinar Prep Materials:</strong></p><ul><li><strong>Quick-Start Guide:</strong> A 5-minute read that sets the context for what we will cover</li><li><strong>Self-Assessment Checklist:</strong> Rate your team on the key areas we will discuss — this will help you ask better questions during the Q&A</li><li><strong>Industry Benchmark Data:</strong> See where teams like {registrant_company} typically stand on the metrics we will explore</li></ul><p>Completing the self-assessment before the webinar will make the session 10x more relevant to your specific situation.</p><p>Your join link (save this): <a href='{join_url}'>{webinar_title}</a></p><p>Looking forward to it!</p><p>The Team</p>",
      ),

      // 6. Community social engagement Slack
      slackNode(
        "6",
        "Engage Community in Pre-Webinar Discussion",
        1240,
        "New registrant for *{webinar_title}*: {registrant_name} from {registrant_company}\\nEvent in {days_until_event} days | Total registrant engaged with pre-event content\\nTip: Reach out for pre-event networking if this is a target account",
      ),

      // 7. 3 days before what-to-expect email
      emailNode(
        "7",
        "3 Days Before: What to Expect Email",
        1480,
        "{registrant_email}",
        "{registrant_name}, your webinar is in 3 days — here is what to expect",
        "<p>Hi {registrant_name},</p><p><strong>{webinar_title}</strong> is just 3 days away! Here is your final preview:</p><p><strong>Session Agenda:</strong></p><ol><li><strong>Introduction and Context Setting</strong> (5 min) — Why this topic matters now</li><li><strong>Core Framework Presentation</strong> (20 min) — The methodology behind top-performing teams</li><li><strong>Live Demo and Examples</strong> (15 min) — See it in action with real use cases</li><li><strong>Q&A with {speaker_names}</strong> (15 min) — Your questions answered live</li><li><strong>Next Steps and Resources</strong> (5 min) — What to do after the webinar</li></ol><p><strong>Logistics:</strong></p><ul><li>Date: {webinar_date}</li><li>Your personal join link: <a href='{join_url}'>Join here</a></li><li>Duration: ~60 minutes</li><li>Recording will be available if you cannot make it, but live attendees get exclusive bonuses</li></ul><p>See you in 3 days!</p><p>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1703. WEBINAR REMINDER 24H AND 1H (7 nodes)
  // ===================================================================

  "Webinar Reminder 24h and 1h": {
    summary:
      "Runs on a schedule 24 hours before the webinar, retrieves the registrant list via API, sends a 24-hour reminder email, waits 23 hours, sends a 1-hour reminder email, sends a final SMS reminder via Twilio, and logs reminder effectiveness to Google Sheets.",
    nodes: [
      trigger(
        "Schedule: 24 hours before webinar for dual reminders",
        "schedule",
        {
          schedule: "24 hours before webinar start",
          howItStarts:
            "Runs 24 hours before a scheduled webinar to send the first reminder, then waits and sends a final 1-hour reminder via email and SMS — a dual-reminder strategy proven to maximize live attendance rates",
          setup:
            "Configure schedule trigger for 24 hours before each webinar. Set webinar platform API for registrant retrieval. Configure email and SMS (Twilio) sending credentials. Set Google Sheets for reminder analytics.",
          inputFields: [],
        },
      ),

      // 2. Registrant list retrieval API
      n("2", "hitApi", "Retrieve Registrant List from API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/webinars/{webinar_id}/registrants?status=approved&page_size=100",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_jwt_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "registrants[0].email",
              customName: "registrant_email",
              type: "string",
            },
            {
              name: "registrants[0].first_name",
              customName: "registrant_name",
              type: "string",
            },
            {
              name: "registrants[0].phone",
              customName: "registrant_phone",
              type: "string",
            },
            {
              name: "registrants[0].join_url",
              customName: "join_url",
              type: "string",
            },
            {
              name: "total_records",
              customName: "total_registrants",
              type: "string",
            },
          ],
        },
      }),

      // 3. 24-hour reminder email
      emailNode(
        "3",
        "Send 24-Hour Reminder Email",
        520,
        "{registrant_email}",
        "Tomorrow: your spot is reserved — do not miss it, {registrant_name}",
        "<p>Hi {registrant_name},</p><p>Just a quick reminder — the webinar you registered for is <strong>tomorrow</strong>!</p><p><strong>Your Personal Join Link:</strong><br/><a href='{join_url}'>Click here to join tomorrow</a></p><p><strong>Quick Checklist:</strong></p><ul><li>Add it to your calendar if you have not already</li><li>Prepare any questions for the live Q&A</li><li>Find a quiet spot with good internet</li><li>Close unnecessary browser tabs before joining</li></ul><p>Pro tip: Join 2-3 minutes early to test your audio and get settled.</p><p>See you tomorrow!</p><p>The Team</p>",
      ),

      // 4. Delay 23 hours
      n("4", "delayAction", "Wait 23 Hours Before 1-Hour Reminder", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 23,
          delayUnit: "hours",
          description:
            "Wait 23 hours after the 24-hour reminder so the next reminders arrive approximately 1 hour before the webinar",
        },
      }),

      // 5. 1-hour reminder email
      emailNode(
        "5",
        "Send 1-Hour Reminder Email",
        1000,
        "{registrant_email}",
        "Starting in 1 hour! Join link inside, {registrant_name}",
        "<p>Hi {registrant_name},</p><p>We start in <strong>60 minutes</strong>!</p><p><a href='{join_url}'><strong>JOIN THE WEBINAR NOW</strong></a></p><p>Last-minute tips:</p><ul><li>Click the link above 2-3 minutes before start</li><li>Use headphones for the best audio experience</li><li>Have your questions ready for Q&A</li></ul><p>See you very soon!</p><p>The Team</p>",
      ),

      // 6. SMS final reminder Twilio
      smsNode(
        "6",
        "Send SMS Final Reminder",
        1240,
        "{registrant_phone}",
        "Hi {registrant_name}! Webinar starts in 1 hour. Join now: {join_url}",
      ),

      // 7. Reminder effectiveness tracking Sheets
      sheetsNode(
        "7",
        "Log Reminder Effectiveness to Sheets",
        1480,
        "WebinarReminders",
        '[["{registrant_name}","{registrant_email}","{registrant_phone}","{total_registrants}","24h_and_1h_dual","email_and_sms","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1704. WHITEPAPER DOWNLOAD NURTURE (9 nodes)
  // ===================================================================

  "Whitepaper Download Nurture": {
    summary:
      "Fires on a whitepaper download via webhook, enriches and scores the lead in HubSpot, updates the CRM record, sends a Day 0 thank you email with download link, waits 3 days, sends a Day 3 key insight deep dive email, waits 4 more days, sends a Day 7 related case study email, and sends a Day 14 consultation demo CTA email.",
    nodes: [
      trigger("Webhook: Whitepaper downloaded", "webhook_app", {
        source: "website",
        event: "content.whitepaper_downloaded",
        howItStarts:
          "Fires when a visitor downloads a gated whitepaper from the website — including the whitepaper title, topic, the visitor's form submission data, and referrer information — to begin a topic-specific nurture sequence",
        setup:
          "Configure website form webhook for whitepaper download events. Set HubSpot API token for lead enrichment and CRM updates. Configure email sending credentials for the multi-week nurture sequence.",
        inputFields: [],
      }),

      // 2. Lead enrichment scoring HubSpot
      n("2", "runCode", "Extract Download Data and Score Lead", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract whitepaper download data and calculate initial lead score
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.form_data?.email || "";
const name = payload.name || payload.form_data?.name || payload.first_name || "";
const company = payload.company || payload.form_data?.company || "";
const title = payload.job_title || payload.form_data?.job_title || "";
const phone = payload.phone || payload.form_data?.phone || "";
const whitepaper_title = payload.whitepaper_title || payload.asset_title || payload.resource_name || "";
const whitepaper_topic = payload.topic || payload.category || "";
const download_url = payload.download_url || payload.asset_url || "";
const referrer = payload.referrer || payload.utm_source || "direct";
let lead_score = 20; // Base score for downloading content
if (title.toLowerCase().includes("vp") || title.toLowerCase().includes("director") || title.toLowerCase().includes("head")) lead_score += 20;
if (title.toLowerCase().includes("cto") || title.toLowerCase().includes("ceo") || title.toLowerCase().includes("cfo")) lead_score += 30;
if (company) lead_score += 10;
if (phone) lead_score += 10;
return {
  lead_email: email,
  lead_name: name,
  lead_company: company,
  lead_title: title,
  lead_phone: phone,
  whitepaper_title: whitepaper_title,
  whitepaper_topic: whitepaper_topic,
  download_url: download_url,
  referrer: referrer,
  initial_lead_score: String(lead_score)
};`,
          outputVariables: [
            { name: "lead_email", type: "string" },
            { name: "lead_name", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "lead_title", type: "string" },
            { name: "lead_phone", type: "string" },
            { name: "whitepaper_title", type: "string" },
            { name: "whitepaper_topic", type: "string" },
            { name: "download_url", type: "string" },
            { name: "referrer", type: "string" },
            { name: "initial_lead_score", type: "string" },
          ],
        },
      }),

      // 3. CRM record update HubSpot
      hubspotPostNode(
        "3",
        "Create or Update Lead in HubSpot",
        520,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_name}","company":"{lead_company}","jobtitle":"{lead_title}","phone":"{lead_phone}","hs_lead_status":"NEW","lead_source":"{referrer}","notes_last_contacted":"Downloaded whitepaper: {whitepaper_title} ({whitepaper_topic}). Lead score: {initial_lead_score}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),

      // 4. Day 0 thank you download link email
      emailNode(
        "4",
        "Day 0: Thank You with Download Link",
        760,
        "{lead_email}",
        "Your whitepaper is ready: {whitepaper_title}",
        "<p>Hi {lead_name},</p><p>Thank you for downloading <strong>{whitepaper_title}</strong>!</p><p><a href='{download_url}'><strong>Download Your Whitepaper Now</strong></a></p><p>This whitepaper covers the latest thinking on {whitepaper_topic}, including:</p><ul><li>Current industry trends and benchmarks</li><li>Proven frameworks used by leading organizations</li><li>Actionable strategies you can implement at {lead_company}</li><li>Common pitfalls and how to avoid them</li></ul><p>We recommend setting aside 15-20 minutes to read through it carefully. The insights in Section 3 are particularly relevant for teams in your position.</p><p>If you have any questions as you read through it, simply reply to this email.</p><p>Happy reading!</p><p>The Team</p>",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 Days Before Key Insights", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after the download to allow the lead time to read the whitepaper before sending deeper insights",
        },
      }),

      // 6. Day 3 key insight deep dive email
      emailNode(
        "6",
        "Day 3: Key Insight Deep Dive Email",
        1240,
        "{lead_email}",
        "The #1 insight from {whitepaper_title} that teams at {lead_company} should act on",
        '<p>Hi {lead_name},</p><p>Hope you have had a chance to dive into <strong>{whitepaper_title}</strong>. If not, no worries — here is the single most impactful takeaway:</p><p><strong>Key Insight:</strong> The gap between top-performing teams and everyone else in {whitepaper_topic} is not about budget or headcount — it is about workflow efficiency and the right automation at the right moments.</p><p>Here is why this matters for {lead_company}:</p><ul><li>Teams that act on this insight see 30-50% efficiency gains within 90 days</li><li>The implementation cost is typically a fraction of the productivity gains</li><li>Early movers in {whitepaper_topic} are creating a compounding advantage</li></ul><p>We put together a supplementary deep-dive that expands on this insight with additional data and examples. Want us to send it over?</p><p>Just reply "yes" and we will get it to you right away.</p><p>Best,<br/>The Team</p>',
      ),

      // 7. Delay 4 days
      n("7", "delayAction", "Wait 4 Days Before Case Study", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 4,
          delayUnit: "days",
          description:
            "Wait 4 more days (Day 7 total) before sending the related case study to maintain nurture cadence",
        },
      }),

      // 8. Day 7 related case study email
      emailNode(
        "8",
        "Day 7: Related Case Study Email",
        1720,
        "{lead_email}",
        "{lead_name}, how a team like {lead_company} put {whitepaper_topic} into practice",
        "<p>Hi {lead_name},</p><p>Last week you downloaded our whitepaper on <strong>{whitepaper_topic}</strong>. Today, we wanted to show you what happens when someone actually puts those insights into practice.</p><p><strong>Case Study: From Whitepaper to Results in 60 Days</strong></p><p>A company similar to {lead_company} read the same whitepaper you downloaded. Here is what happened next:</p><ul><li><strong>Week 1-2:</strong> Audited their current {whitepaper_topic} processes against the framework in the whitepaper</li><li><strong>Week 3-4:</strong> Implemented the two highest-impact recommendations</li><li><strong>Week 5-8:</strong> Saw a 45% improvement in the key metrics we outlined</li></ul><p><strong>The result:</strong> Full ROI within 60 days and a foundation for ongoing optimization.</p><p>The difference between reading a whitepaper and getting results like these? A conversation about how to apply it to your specific situation.</p><p>Worth a 15-minute chat? <a href='https://calendly.com/your-team/whitepaper-followup?email={lead_email}'>Pick a time here</a></p><p>Best,<br/>The Team</p>",
      ),

      // 9. Day 14 consultation demo CTA email
      emailNode(
        "9",
        "Day 14: Consultation Demo CTA Email",
        1960,
        "{lead_email}",
        "Last chance: Free strategy session for {whitepaper_title} readers",
        "<p>Hi {lead_name},</p><p>It has been two weeks since you downloaded <strong>{whitepaper_title}</strong>, and we wanted to extend one final offer.</p><p><strong>Exclusive for whitepaper readers:</strong> A complimentary 30-minute strategy session where we will:</p><ol><li>Assess your current {whitepaper_topic} maturity at {lead_company}</li><li>Identify the 2-3 highest-impact opportunities specific to your team</li><li>Create a prioritized 90-day action plan</li><li>Share benchmarks from similar organizations</li></ol><p>This is not a sales pitch — it is a genuine strategy consultation. We do these for a select number of whitepaper readers each month, and spots fill up quickly.</p><p><a href='https://calendly.com/your-team/strategy-session?email={lead_email}'><strong>Reserve Your Strategy Session</strong></a></p><p>If the timing is not right, no pressure at all. We will continue sharing relevant insights periodically.</p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1705. WORKSHOP HOMEWORK REMINDERS (7 nodes)
  // ===================================================================

  "Workshop Homework Reminders": {
    summary:
      "Fires when a workshop session ends via webhook, fetches homework assignment details with runCode, sends an immediate post-session email, sends a mid-point reminder with delay, sends a 24-hours-before-due reminder email, checks submission tracking via condition, and sends a non-submission follow-up email.",
    nodes: [
      trigger("Webhook: Workshop session concluded", "webhook_app", {
        source: "workshop_platform",
        event: "session.ended",
        howItStarts:
          "Fires when a workshop session ends — including the session topic, homework assignment details, due date, and participant information — to trigger a homework reminder sequence that drives assignment completion",
        setup:
          "Configure workshop platform webhook for session end events. Set OpenAI API key for content extraction. Configure email sending credentials for the homework reminder sequence.",
        inputFields: [],
      }),

      // 2. Homework assignment fetch runCode
      n("2", "runCode", "Extract Homework Assignment Details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse session end webhook and extract homework details
const payload = JSON.parse(vars.webhook_payload || "{}");
const participant_email = payload.participant_email || payload.email || payload.attendee?.email || "";
const participant_name = payload.participant_name || payload.name || payload.attendee?.name || "";
const session_title = payload.session_title || payload.topic || "";
const session_number = payload.session_number || payload.session_id || "1";
const homework_title = payload.homework_title || payload.assignment_title || "";
const homework_description = payload.homework_description || payload.assignment_details || "";
const homework_due_date = payload.due_date || payload.homework_due || "";
const submission_url = payload.submission_url || payload.upload_url || "";
const instructor_name = payload.instructor || payload.facilitator || "";
const cohort_name = payload.cohort || payload.group || "";
const now = new Date();
const dueDate = new Date(homework_due_date || now);
dueDate.setDate(dueDate.getDate() + 7); // Default 7 days if no due date
const daysUntilDue = Math.max(0, Math.round((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
const midpointDays = Math.max(1, Math.floor(daysUntilDue / 2));
const has_submitted = "false";
return {
  participant_email: participant_email,
  participant_name: participant_name,
  session_title: session_title,
  session_number: session_number,
  homework_title: homework_title,
  homework_description: homework_description,
  homework_due_date: dueDate.toISOString().split("T")[0],
  submission_url: submission_url,
  instructor_name: instructor_name,
  cohort_name: cohort_name,
  days_until_due: String(daysUntilDue),
  midpoint_delay_days: String(midpointDays),
  has_submitted: has_submitted
};`,
          outputVariables: [
            { name: "participant_email", type: "string" },
            { name: "participant_name", type: "string" },
            { name: "session_title", type: "string" },
            { name: "session_number", type: "string" },
            { name: "homework_title", type: "string" },
            { name: "homework_description", type: "string" },
            { name: "homework_due_date", type: "string" },
            { name: "submission_url", type: "string" },
            { name: "instructor_name", type: "string" },
            { name: "cohort_name", type: "string" },
            { name: "days_until_due", type: "string" },
            { name: "midpoint_delay_days", type: "string" },
            { name: "has_submitted", type: "string" },
          ],
        },
      }),

      // 3. Immediate post-session email
      emailNode(
        "3",
        "Send Post-Session Homework Email",
        520,
        "{participant_email}",
        "Session {session_number} complete! Here is your homework: {homework_title}",
        "<p>Hi {participant_name},</p><p>Great work in today's session on <strong>{session_title}</strong>!</p><p><strong>Your Homework Assignment:</strong></p><p><strong>{homework_title}</strong></p><p>{homework_description}</p><p><strong>Details:</strong></p><ul><li><strong>Due Date:</strong> {homework_due_date} ({days_until_due} days from now)</li><li><strong>Submission:</strong> <a href='{submission_url}'>Submit your homework here</a></li><li><strong>Instructor:</strong> {instructor_name}</li><li><strong>Cohort:</strong> {cohort_name}</li></ul><p><strong>Tips for Success:</strong></p><ol><li>Start within 24 hours while the session content is fresh</li><li>Block 45-60 minutes on your calendar specifically for this</li><li>Refer back to the session recording if you need a refresher</li><li>Do not aim for perfection — aim for thoughtful completion</li></ol><p>Questions? Reply to this email or reach out to {instructor_name} directly.</p><p>Good luck!</p><p>The Team</p>",
      ),

      // 4. Mid-point reminder delay + email
      n("4", "delayAction", "Wait Until Mid-Point Before Reminder", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait until approximately the mid-point of the homework period before sending the first reminder",
        },
      }),

      // 5. Mid-point reminder email
      emailNode(
        "5",
        "Send Mid-Point Homework Reminder",
        1000,
        "{participant_email}",
        "Halfway check-in: How is {homework_title} coming along?",
        "<p>Hi {participant_name},</p><p>Quick check-in! You are about halfway to the due date for <strong>{homework_title}</strong> from Session {session_number} ({session_title}).</p><p><strong>Status Check:</strong></p><ul><li>If you have started: Great! Keep the momentum going</li><li>If you have not started yet: No worries — here is a quick refresher on what to do</li></ul><p><strong>Quick Refresher:</strong></p><p>{homework_description}</p><p><strong>Due:</strong> {homework_due_date}<br/><strong>Submit:</strong> <a href='{submission_url}'>Upload here</a></p><p>Remember: the homework is designed to reinforce what you learned in the session. Completing it is the #1 predictor of long-term skill retention.</p><p>You have got this!</p><p>The Team</p>",
      ),

      // 6. Submission tracking condition
      n("6", "condition", "Has Participant Submitted Homework?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "has_submitted", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Non-submission follow-up email
      emailNode(
        "7",
        "Send Non-Submission Follow-Up Email",
        1480,
        "{participant_email}",
        "Due tomorrow: {homework_title} — we are here to help",
        "<p>Hi {participant_name},</p><p>Friendly reminder — <strong>{homework_title}</strong> is due tomorrow ({homework_due_date}).</p><p>We noticed you have not submitted yet, and we want to make sure you are not stuck. Here are some options:</p><ul><li><strong>Need more time?</strong> Reply to this email and we will work with you on an extension</li><li><strong>Stuck on a specific part?</strong> {instructor_name} offers office hours — reach out for guidance</li><li><strong>Feeling overwhelmed?</strong> Submit what you have — partial completion is better than no completion</li></ul><p><strong>Submit:</strong> <a href='{submission_url}'>Upload your homework here</a></p><p>The purpose of this assignment is to help you internalize the skills from {session_title}. Even a rough attempt will significantly boost your retention.</p><p>We are rooting for you!</p><p>{instructor_name} and The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1706. AE-SDR HANDOFF AUTOMATION (9 nodes)
  // ===================================================================

  "AE-SDR Handoff Automation": {
    summary:
      "Fires when lead qualification is complete via webhook, validates handoff data with runCode, generates an AI handoff brief, selects and assigns the AE via condition, sends a warm introduction email, notifies the AE on Slack, updates CRM stage and ownership in HubSpot, monitors handoff SLA with a 4-hour delay, and captures handoff quality feedback with a follow-up delay.",
    nodes: [
      trigger("Webhook: Lead qualification completed by SDR", "webhook_app", {
        source: "hubspot",
        event: "deal.stage_change.qualified",
        howItStarts:
          "Fires when an SDR completes lead qualification and moves a deal to the qualified stage — triggering a structured handoff to the assigned Account Executive with all context, talking points, and engagement history",
        setup:
          "Configure HubSpot webhook for deal stage changes to qualified. Set OpenAI API key for handoff brief generation. Configure Slack webhook for AE notifications. Provide HubSpot token for CRM stage and ownership updates.",
        inputFields: [],
      }),

      // 2. Handoff data validation runCode
      n("2", "runCode", "Validate and Compile Handoff Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and validate all handoff data from webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const deal_id = payload.deal_id || payload.objectId || payload.id || "";
const deal_name = payload.dealname || payload.properties?.dealname || "";
const deal_amount = payload.amount || payload.properties?.amount || "";
const contact_email = payload.contact_email || payload.properties?.contact_email || "";
const contact_name = payload.contact_name || payload.properties?.contact_name || "";
const contact_title = payload.contact_title || payload.properties?.jobtitle || "";
const company_name = payload.company || payload.properties?.company || "";
const sdr_name = payload.sdr_name || payload.owner_name || "";
const sdr_email = payload.sdr_email || payload.owner_email || "";
const qual_notes = payload.qualification_notes || payload.notes || "";
const pain_points = payload.pain_points || "";
const budget = payload.budget || payload.properties?.budget || "";
const timeline = payload.timeline || payload.properties?.timeline || "";
const decision_maker = payload.decision_maker || "";
const competitors = payload.competitors_mentioned || "";
const hasAllRequiredFields = !!(deal_id && contact_email && contact_name);
const handoff_quality = hasAllRequiredFields ? "complete" : "incomplete";
const ae_territory = company_name.length > 10 ? "enterprise" : "mid_market";
return {
  deal_id: deal_id,
  deal_name: deal_name,
  deal_amount: deal_amount,
  contact_email: contact_email,
  contact_name: contact_name,
  contact_title: contact_title,
  company_name: company_name,
  sdr_name: sdr_name,
  sdr_email: sdr_email,
  qual_notes: qual_notes,
  pain_points: pain_points,
  budget: budget,
  timeline: timeline,
  decision_maker: decision_maker,
  competitors: competitors,
  handoff_quality: handoff_quality,
  ae_territory: ae_territory
};`,
          outputVariables: [
            { name: "deal_id", type: "string" },
            { name: "deal_name", type: "string" },
            { name: "deal_amount", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "contact_title", type: "string" },
            { name: "company_name", type: "string" },
            { name: "sdr_name", type: "string" },
            { name: "sdr_email", type: "string" },
            { name: "qual_notes", type: "string" },
            { name: "pain_points", type: "string" },
            { name: "budget", type: "string" },
            { name: "timeline", type: "string" },
            { name: "decision_maker", type: "string" },
            { name: "competitors", type: "string" },
            { name: "handoff_quality", type: "string" },
            { name: "ae_territory", type: "string" },
          ],
        },
      }),

      // 3. AI handoff brief generation
      aiNode(
        "3",
        "AI Generate Handoff Brief",
        520,
        "You are a sales handoff specialist. Create a comprehensive handoff brief for an Account Executive who is taking over a qualified lead from an SDR. Include a summary of the opportunity, key talking points, potential objections and how to handle them, recommended next steps, and the ideal meeting agenda for the first AE call. Respond JSON: {opportunity_summary: string, key_talking_points: string, potential_objections: string, objection_responses: string, recommended_agenda: string, intro_email_subject: string, intro_email_body_html: string, urgency_level: string}",
        "Deal: {deal_name} (${deal_amount}). Contact: {contact_name} ({contact_title} at {company_name}). SDR: {sdr_name}. Qualification notes: {qual_notes}. Pain points: {pain_points}. Budget: {budget}. Timeline: {timeline}. Decision maker: {decision_maker}. Competitors: {competitors}. Territory: {ae_territory}. Generate comprehensive handoff brief.",
        [
          {
            field: "opportunity_summary",
            as: "opportunity_summary",
            type: "string",
          },
          {
            field: "key_talking_points",
            as: "key_talking_points",
            type: "string",
          },
          {
            field: "potential_objections",
            as: "potential_objections",
            type: "string",
          },
          {
            field: "objection_responses",
            as: "objection_responses",
            type: "string",
          },
          {
            field: "recommended_agenda",
            as: "recommended_agenda",
            type: "string",
          },
          {
            field: "intro_email_subject",
            as: "intro_email_subject",
            type: "string",
          },
          {
            field: "intro_email_body_html",
            as: "intro_email_body",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 4. AE selection assignment condition
      n("4", "condition", "Is Enterprise Territory?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "ae_territory", operator: "eq", value: "enterprise" },
          ],
        },
      }),

      // 5. Warm introduction email
      emailNode(
        "5",
        "Send Warm Introduction Email",
        1000,
        "{contact_email}",
        "{intro_email_subject}",
        "{intro_email_body}",
      ),

      // 6. AE Slack notification
      slackNode(
        "6",
        "Notify AE on Slack",
        1240,
        "New Qualified Handoff: *{deal_name}* (${deal_amount})\\nContact: *{contact_name}* — {contact_title} at {company_name}\\nTerritory: {ae_territory} | Urgency: {urgency_level}\\nSDR: {sdr_name} | Quality: {handoff_quality}\\n\\n*Summary:* {opportunity_summary}\\n\\n*Key Talking Points:* {key_talking_points}\\n\\n*Potential Objections:* {potential_objections}\\n*How to Handle:* {objection_responses}\\n\\n*Recommended First Call Agenda:* {recommended_agenda}\\n\\nBudget: {budget} | Timeline: {timeline} | Decision Maker: {decision_maker} | Competitors: {competitors}\\nHubSpot: https://app.hubspot.com/deals/{deal_id}",
      ),

      // 7. CRM stage ownership update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM Stage and Ownership",
        1480,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"dealstage":"qualifiedtobuy","pipeline":"default","notes_last_contacted":"AE handoff complete. Brief generated. Intro email sent. Summary: {opportunity_summary}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "new_deal_stage",
            type: "string",
          },
        ],
      ),

      // 8. Handoff SLA monitor delay 4hrs
      n("8", "delayAction", "Wait 4 Hours for Handoff SLA Check", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 4,
          delayUnit: "hours",
          description:
            "Wait 4 hours after the handoff to check if the AE has responded and picked up the deal within the SLA window",
        },
      }),

      // 9. Handoff quality feedback loop delay
      n("9", "delayAction", "Wait 48 Hours for Quality Feedback", 1960, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
          description:
            "Wait 48 hours after handoff to trigger a quality feedback loop — checking if the first AE meeting was booked and gathering feedback on handoff completeness",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1707. AI COLD CALL OPENER GENERATOR (7 nodes)
  // ===================================================================

  "AI Cold Call Opener Generator": {
    summary:
      "Fires when a call prep request arrives via webhook, gathers prospect intelligence from Clearbit API, detects trigger events with runCode, generates AI cold call openers, generates AI talk track variants, delivers the pack to the rep on Slack, and tracks outcomes in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Cold call prep request", "webhook_app", {
        source: "slack",
        event: "slash_command.callprep",
        howItStarts:
          "Fires when a sales rep triggers the /callprep slash command in Slack with a prospect email — requesting real-time prospect intelligence, personalized cold call openers, and talk track variants before making the call",
        setup:
          "Configure Slack slash command /callprep to post to webhook URL. Set Clearbit API key for prospect intelligence. Set OpenAI API key for opener and talk track generation. Provide HubSpot token for outcome tracking.",
        inputFields: [],
      }),

      // 2. Prospect intelligence gathering Clearbit API
      n("2", "runCode", "Parse Slack Command and Extract Prospect Email", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the Slack slash command to get the prospect email
const payload = JSON.parse(vars.webhook_payload || "{}");
const command_text = payload.text || payload.command_text || "";
const rep_name = payload.user_name || payload.user?.name || "";
const rep_id = payload.user_id || payload.user?.id || "";
const channel_id = payload.channel_id || "";
const prospect_email = command_text.trim().toLowerCase();
return {
  prospect_email: prospect_email,
  rep_name: rep_name,
  rep_id: rep_id,
  channel_id: channel_id
};`,
          outputVariables: [
            { name: "prospect_email", type: "string" },
            { name: "rep_name", type: "string" },
            { name: "rep_id", type: "string" },
            { name: "channel_id", type: "string" },
          ],
        },
      }),

      // 3. Clearbit prospect intelligence
      n("3", "hitApi", "Gather Prospect Intelligence from Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={prospect_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "prospect_name",
              type: "string",
            },
            { name: "title", customName: "prospect_title", type: "string" },
            { name: "role", customName: "prospect_role", type: "string" },
            {
              name: "seniority",
              customName: "prospect_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "prospect_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "company.tech", customName: "tech_stack", type: "string" },
            {
              name: "company.description",
              customName: "company_description",
              type: "string",
            },
            {
              name: "location",
              customName: "prospect_location",
              type: "string",
            },
            { name: "bio", customName: "prospect_bio", type: "string" },
          ],
        },
      }),

      // 4. Trigger event detection runCode
      n("4", "runCode", "Detect Trigger Events and Build Context", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze prospect data to detect trigger events and build call context
const title = (vars.prospect_title || "").toLowerCase();
const seniority = (vars.prospect_seniority || "").toLowerCase();
const industry = (vars.company_industry || "").toLowerCase();
const employees = parseInt(vars.company_size) || 0;
const revenue = vars.company_revenue || "";
const bio = vars.prospect_bio || "";
const tech = vars.tech_stack || "";
let personaType = "individual_contributor";
if (seniority.includes("executive") || seniority.includes("c-level")) personaType = "executive";
else if (seniority.includes("director") || seniority.includes("vp")) personaType = "senior_leader";
else if (seniority.includes("manager")) personaType = "manager";
let trigger_events = [];
if (employees > 100 && employees < 500) trigger_events.push("growth_stage_company");
if (employees > 500) trigger_events.push("enterprise_scale");
if (bio.toLowerCase().includes("new role") || bio.toLowerCase().includes("recently joined")) trigger_events.push("new_in_role");
if (tech.toLowerCase().includes("salesforce") || tech.toLowerCase().includes("hubspot")) trigger_events.push("crm_user");
const triggerEventStr = trigger_events.length > 0 ? trigger_events.join(", ") : "no_specific_triggers";
const callDifficulty = personaType === "executive" ? "high" : personaType === "senior_leader" ? "medium" : "standard";
return {
  persona_type: personaType,
  trigger_events: triggerEventStr,
  call_difficulty: callDifficulty,
  company_context: vars.prospect_company + " | " + industry + " | " + employees + " employees | $" + revenue + " revenue",
  prospect_summary: vars.prospect_name + " (" + vars.prospect_title + ") at " + vars.prospect_company
};`,
          outputVariables: [
            { name: "persona_type", type: "string" },
            { name: "trigger_events", type: "string" },
            { name: "call_difficulty", type: "string" },
            { name: "company_context", type: "string" },
            { name: "prospect_summary", type: "string" },
          ],
        },
      }),

      // 5. AI opener generation
      aiNode(
        "5",
        "AI Generate Cold Call Openers",
        1000,
        "You are an elite B2B cold calling coach. Given prospect intelligence including their role, seniority, company details, industry, trigger events, and persona type, generate 3 distinct cold call openers that feel natural and conversational. Each opener should use a different approach: (1) trigger-event based, (2) industry-insight based, (3) peer-reference based. Also generate a voicemail script. Respond JSON: {opener_trigger: string, opener_industry: string, opener_peer: string, voicemail_script: string, opening_question: string, value_hook: string, objection_bridge: string}",
        "Prospect: {prospect_name} ({prospect_title}, {prospect_seniority}) at {prospect_company}. Industry: {company_industry}. Size: {company_size} employees. Revenue: ${company_revenue}. Persona: {persona_type}. Trigger events: {trigger_events}. Bio: {prospect_bio}. Tech stack: {tech_stack}. Company: {company_description}. Generate 3 openers and a voicemail script.",
        [
          { field: "opener_trigger", as: "opener_trigger", type: "string" },
          { field: "opener_industry", as: "opener_industry", type: "string" },
          { field: "opener_peer", as: "opener_peer", type: "string" },
          { field: "voicemail_script", as: "voicemail_script", type: "string" },
          { field: "opening_question", as: "opening_question", type: "string" },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "objection_bridge", as: "objection_bridge", type: "string" },
        ],
      ),

      // 6. AI talk track variants
      aiNode(
        "6",
        "AI Generate Talk Track Variants",
        1240,
        "You are a cold call talk track architect. Given the prospect profile, openers, and objection bridges already generated, create 2 complete talk track variants: (1) a direct/assertive track for when the prospect is receptive, and (2) a consultative/question-led track for when the prospect is guarded. Each track should include the flow from opener to qualification to next-step close. Respond JSON: {direct_track: string, consultative_track: string, qualification_questions: string, next_step_close: string, time_check_script: string}",
        "Prospect: {prospect_summary}. Persona: {persona_type}. Difficulty: {call_difficulty}. Trigger events: {trigger_events}. Value hook: {value_hook}. Opening question: {opening_question}. Objection bridge: {objection_bridge}. Generate 2 complete talk track variants.",
        [
          { field: "direct_track", as: "direct_track", type: "string" },
          {
            field: "consultative_track",
            as: "consultative_track",
            type: "string",
          },
          {
            field: "qualification_questions",
            as: "qualification_questions",
            type: "string",
          },
          { field: "next_step_close", as: "next_step_close", type: "string" },
          {
            field: "time_check_script",
            as: "time_check_script",
            type: "string",
          },
        ],
      ),

      // 7. Rep delivery Slack
      slackNode(
        "7",
        "Deliver Call Prep Pack to Rep on Slack",
        1480,
        "Call Prep for <@{rep_id}>: *{prospect_summary}*\\n\\n*Prospect Intel:*\\n{company_context}\\nPersona: {persona_type} | Difficulty: {call_difficulty}\\nTrigger Events: {trigger_events}\\nLocation: {prospect_location}\\n\\n*Opener 1 (Trigger):* {opener_trigger}\\n*Opener 2 (Industry):* {opener_industry}\\n*Opener 3 (Peer):* {opener_peer}\\n\\n*Opening Question:* {opening_question}\\n*Value Hook:* {value_hook}\\n*Objection Bridge:* {objection_bridge}\\n\\n*Direct Track:* {direct_track}\\n*Consultative Track:* {consultative_track}\\n\\n*Qualification Qs:* {qualification_questions}\\n*Next-Step Close:* {next_step_close}\\n*Time Check:* {time_check_script}\\n\\n*Voicemail:* {voicemail_script}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
